import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { validateFileSize } from '../lib/uploadUtils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { calculateFileSha256 } from '../lib/evidenceHash';

// Import Step components for modular form rendering
import { Step1BasicSelection } from '../modules/ghg/emissions/shared/components/steps/Step1BasicSelection';
import { Step2ProcessResponsibility } from '../modules/ghg/emissions/shared/components/steps/Step2ProcessResponsibility';
import { Step3YearMonthlyData } from '../modules/ghg/emissions/shared/components/steps/Step3YearMonthlyData';
import { Step4Notes } from '../modules/ghg/emissions/shared/components/steps/Step4Notes';
import { EmissionFormSection } from '../modules/ghg/emissions/shared/components/EmissionFormSection';
import { ReportingPeriodControls } from '../modules/ghg/emissions/shared/components/ReportingPeriodControls';
import { categoryRegistry } from '../modules/emissions';
import {
  MONTHS,
  CALENDAR_YEAR_MONTHS,
  FINANCIAL_YEAR_MONTHS,
} from '../modules/ghg/emissions/shared/constants/emission-form-constants';
import useEmissionFormState from '../modules/ghg/emissions/shared/hooks/useEmissionFormState';
import useEmissionFormEffects from '../modules/ghg/emissions/shared/hooks/useEmissionFormEffects';
import useEmissionSubmit from '../modules/ghg/emissions/shared/hooks/useEmissionSubmit';
import useSpendCurrencyDefaults from '../modules/ghg/emissions/shared/hooks/useSpendCurrencyDefaults';
import { canProceedToStep as canProceedToStepUtil } from '../modules/ghg/emissions/shared/utils/validation';
import { resolveEmissionQuantity } from '../modules/ghg/emissions/shared/utils/emissionQuantity';
import {
  DynamicFieldRenderer,
  getFieldUnits as getFieldUnitsShared,
} from '../modules/ghg/emissions/shared/components/DynamicFieldRenderer';
import { getCategoryFuelAllowedUnits } from '../modules/ghg/emissions/shared/utils/fuelUnits';
import { selectClosestScope3Factors } from '../modules/ghg/emissions/shared/utils/scope3FactorYear';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createC6TripId = () => `c6-trip-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const EMPTY_SPEND_CURRENCY_MAP = {};


// Helper to check if unit is volume-based (from centralized units)
const isVolumeUnit = (unit, centralizedUnits = []) => {
  const unitDef = centralizedUnits.find(u => u.symbol?.toLowerCase() === unit?.toLowerCase());
  return unitDef?.unit_type === 'volume';
};

// Density dimension-mismatch helpers (canonical source: shared/utils/unitHelpers.js)
// isDensityRequiredForQtyBasis moved with the field derivation into modules/ghg/config.
import {
  isDensityRequiredForHeatBasis,
  isDensityRequiredForCarbonComposition,
  prepareDensityAwareCalculationInputs,
  normalizeCustomFuelCalorificValueUnit,
  normalizeCustomFuelCompoundUnit,
  normalizeCustomFuelDensityUnit,
  normalizeCustomFuelQuantityUnit,
  resolveCompoundDenominatorBasis,
  resolveProcessEfDenominatorBasis,
} from '../modules/ghg/emissions/shared/utils/unitHelpers';
import { isMonthlyEntryComplete } from '../modules/ghg/emissions/shared/utils/monthlyCompletion';
import { normalizeFuelBackedFieldUnits } from '../modules/ghg/emissions/shared/utils/monthlyFieldUnits';
// Shared GHG configuration layer: resolved config + explicit context -> fields
import {
  deriveGhgFields,
  resolveGhgFormContext,
  resolveEffectiveScopeCode,
  resolveGhgFormArchitecture,
  resolveGhgCategoryOptions,
  resolveGhgScope3Options,
  resolveScope3MethodsForCategory,
  GHG_FIELD_OPTION_KEYS,
} from '../modules/ghg/config';
import { buildCustomFuelCalculationPayload } from '../pages/emissions/utils/customFuelCalcAdapter';
import { filterSupplierVisibleCategories } from '../modules/supplier-assessment/utils/supplierScopeAccess';

// Helper to check if a month/year combination is in the future
const isFutureMonth = (monthKey, year, yearType = 'calendar') => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12
  
  let selectedYear = parseInt(year);
  const selectedMonth = parseInt(monthKey);
  
  // For financial year: Jan-Mar belong to next calendar year
  if (yearType === 'financial' && selectedMonth >= 1 && selectedMonth <= 3) {
    selectedYear = selectedYear + 1;
  }
  
  if (selectedYear > currentYear) return true;
  if (selectedYear === currentYear && selectedMonth > currentMonth) return true;
  return false;
};

export default function EmissionEntryForm({
  facilities,
  fuelDatabase,
  centralizedUnits,
  formulaDefinitions = [],
  formulaParameters = [],
  emissionConfigurations = [],
  gwpConfig = null,
  dynamicScopes = [],
  dynamicCategories = [],
  hasScope3Access = false,
  getAuthHeader,
  onSuccess,
  onCancel,
  onFormChange, // Callback when form becomes dirty (#19)
  editingEmission = null,
  configLabels = null, // Centralized label configuration
  organization = null, // Organization data for reporting year type
  // KPI Access Control props
  kpiAccessInfo = null,
  kpiCanAccessScope = null,
  kpiCanAccessPeriod = null,
  kpiGetPeriodRestrictions = null,
  kpiAllowedScopes = null,
  kpiPeriodRestrictions = null,
  filterFacilitiesByScope = null,
  hasFullKPIAccess = true,
  // Supplier context for supplier portal emissions
  supplierContext = null,
  assignedReportingPeriod = null,
  // OCR Prefill Data - from AI Invoice Extractor workflow
  ocrPrefillData = null,
  // Future organization-specific GHG configuration. `null` today, which makes
  // resolveGhgConfig return the standard configuration untouched.
  organizationGhgOverrides = null,
  supplierGhgConfig = null,
}) {
  // Helper to get method labels from centralized config (no hardcoded fallbacks)
  const getMethodLabel = useCallback((method, short = false) => {
    if (!method) return '-';
    if (configLabels) {
      const labels = short ? configLabels.calculation_methods_short : configLabels.calculation_methods;
      return labels?.[method] || method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, [configLabels]);
  
  // Helper to get subcategory labels from centralized config
  const getSubcategoryLabel = useCallback((subcategory) => {
    if (!subcategory) return '-';
    if (configLabels?.subcategories) {
      return configLabels.subcategories[subcategory] || subcategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return subcategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, [configLabels]);

  // ============================================================================
  // F2: Centralized form state — replaces ~60 inline useState calls.
  // The hook also owns 4 useEffects (org year-type sync, decisionFieldValues sync,
  // custom-activity auto-enable, editingEmission frequency_type/yearlyData load).
  // Those inline useEffects below this block were removed during F2 integration.
  // ============================================================================
  const _formState = useEmissionFormState({ organization, editingEmission, assignedReportingPeriod });
  const {
    // Step 1: Basic Selection
    facilityId, setFacilityId,
    scope, setScope,
    category, setCategory,
    fuelId, setFuelId,
    useCustomFuel, setUseCustomFuel,
    customFuelName, setCustomFuelName,
    customEmissionFactor, setCustomEmissionFactor,
    customEmissionFactorUnit, setCustomEmissionFactorUnit,
    customSource, setCustomSource,
    isSaving, setIsSaving,
    fuelSearchTerm, setFuelSearchTerm,
    // Scope 3
    scope3Method, setScope3Method,
    spendCurrencyConversionMethod, setSpendCurrencyConversionMethod,
    allocationMethod, setAllocationMethod,
    scope3EFData, setScope3EFData,
    scope3ActivityId, setScope3ActivityId,
    scope3ActivityType, setScope3ActivityType,
    scope3Subcategory, setScope3Subcategory,
    typeOfProduct, setTypeOfProduct,
    scope3CustomActivity, setScope3CustomActivity,
    useCustomActivity, setUseCustomActivity,
    fugitiveEmissionsData, setFugitiveEmissionsData,
    loadingScope3EF, setLoadingScope3EF,
    assetName, setAssetName,
    fromLocation, setFromLocation,
    toLocation, setToLocation,
    // Biogenic
    biogenicScopeSelection, setBiogenicScopeSelection,
    biogenicCategories, setBiogenicCategories,
    loadingBiogenicCategories, setLoadingBiogenicCategories,
    // Multi-Employee (C7)
    employees, setEmployees,
    employeeMonthlyTotals, setEmployeeMonthlyTotals,
    employeeYearlyTotal, setEmployeeYearlyTotal,
    isCalculatingEmployee, setIsCalculatingEmployee,
    c7FormulaId, setC7FormulaId,
    c7FormulaName, setC7FormulaName,
    c6Trips, setC6Trips,
    // Decision tree
    decisionFieldValues, setDecisionFieldValues,
    // Dynamic Form Config (Calc Engine)
    formConfig, setFormConfig,
    loadingFormConfig, setLoadingFormConfig,
    calcEngineResult, setCalcEngineResult,
    isCalcEngineCalculating, setIsCalcEngineCalculating,
    matchedFormulaId, setMatchedFormulaId,
    // Step 2: Process & Responsibility
    processNames, setProcessNames,
    responsiblePerson, setResponsiblePerson,
    responsiblePersonDesignation, setResponsiblePersonDesignation,
    responsiblePersonContact, setResponsiblePersonContact,
    // Step 3: Year & Monthly Data
    reportingYearType, setReportingYearType,
    reportingYear, setReportingYear,
    frequencyType, setFrequencyType,
    monthlyData, setMonthlyData,
    yearlyData, setYearlyData,
    yearlyCalcResult, setYearlyCalcResult,
    isCalculatingYearly, setIsCalculatingYearly,
    expandedMonths, setExpandedMonths,
    // Step 4: Notes
    notes, setNotes,
    // Step 2 (optional, common): Record Source
    recordSource, setRecordSource,
    // Scope 3 optional fields
    supplierName, setSupplierName,
    supplierCode, setSupplierCode,
    employeeName, setEmployeeName,
    employeeId, setEmployeeId,
  } = _formState;

  // ============================================================================
  // F3: Centralized data-fetching effects — replaces 5 inline useEffects
  // (form-config fetch, fugitive emissions fetch, scope3-ef fetch, biogenic
  // categories fetch, biogenic scope3-ef fetch). Logic byte-identical.
  // ============================================================================
  useEmissionFormEffects({
    scope,
    category,
    biogenicScopeSelection,
    dynamicCategories,
    useCustomFuel,
    getAuthHeader,
    setFormConfig,
    setLoadingFormConfig,
    setCalcEngineResult,
    setScope3EFData,
    setLoadingScope3EF,
    setBiogenicCategories,
    setLoadingBiogenicCategories,
    setFugitiveEmissionsData,
  });

  // ============================================================================
  // EDIT MODE HYDRATION
  // Auto-populate form fields from editingEmission when editing
  // Uses hydrateEmissionForm utility for comprehensive, consistent hydration
  // ============================================================================
  useEffect(() => {
    if (!editingEmission) return;
    
    // Import the hydration utility dynamically to avoid circular deps
    import('../pages/emissions/utils/hydrateEmissionForm').then(({ hydrateEmissionForm }) => {
      const hydrated = hydrateEmissionForm(editingEmission, {
        fuelDatabase,
        scope3EFData,
        fugitiveEmissionsData,
      });
      
      // Hydrate basic selection fields (Step 1)
      if (editingEmission.facility_id) {
        setFacilityId(editingEmission.facility_id);
      }
      if (editingEmission.scope) {
        setScope(editingEmission.scope);
      }
      if (editingEmission.category) {
        setCategory(editingEmission.category);
      }
      if (editingEmission.fuel_database_id) {
        setFuelId(editingEmission.fuel_database_id);
      } else if (editingEmission.fuel_type) {
        // Try to find matching fuel by name
        const matchingFuel = fuelDatabase.find(f => 
          f.fuel_name === editingEmission.fuel_type || 
          f.name === editingEmission.fuel_type
        );
        if (matchingFuel) {
          setFuelId(matchingFuel.id);
        }
      }
      
      // Hydrate frequency type
      if (hydrated.frequencyType) {
        setFrequencyType(hydrated.frequencyType);
      }
      
      // Hydrate reporting period
      if (assignedReportingPeriod?.reporting_year) {
        setReportingYear(assignedReportingPeriod.reporting_year);
        setReportingYearType(assignedReportingPeriod.reporting_year_type);
      } else if (editingEmission.reporting_period) {
        const rp = editingEmission.reporting_period;
        // Extract year from reporting period (e.g., "2024-01" or "FY2024")
        const yearMatch = rp.match(/(\d{4})/);
        if (yearMatch) {
          setReportingYear(yearMatch[1]);
        }
      }
      
      // Hydrate notes
      if (editingEmission.notes) {
        setNotes(editingEmission.notes);
      }
      
      // Hydrate responsible person info
      if (editingEmission.responsible_person) {
        setResponsiblePerson(editingEmission.responsible_person);
      }
      if (editingEmission.responsible_person_designation) {
        setResponsiblePersonDesignation(editingEmission.responsible_person_designation);
      }
      if (editingEmission.responsible_person_contact) {
        setResponsiblePersonContact(editingEmission.responsible_person_contact);
      }
      
      // Hydrate biogenic scope selection using hydrated values
      if (editingEmission.scope === 'biogenic') {
        setBiogenicScopeSelection(hydrated.biogenicScopeSelection);
      }
      
      // Hydrate Scope 3 fields using hydrated values
      if (editingEmission.scope === 'scope3' || 
          (editingEmission.scope === 'biogenic' && hydrated.biogenicScopeSelection === 'scope3')) {
        setScope3Method(hydrated.scope3Method);
        setSpendCurrencyConversionMethod(hydrated.spendCurrencyConversionMethod || 'standard');
        setScope3ActivityType(hydrated.scope3ActivityType);
        setScope3Subcategory(hydrated.scope3Subcategory);
        setTypeOfProduct(hydrated.typeOfProduct);
        setScope3ActivityId(hydrated.scope3ActivityId);
        setScope3CustomActivity(hydrated.scope3CustomActivity);
        setUseCustomActivity(hydrated.useCustomActivity);
        
        // Hydrate employee data for C7
        if (hydrated.employees?.length > 0) {
          setEmployees(hydrated.employees);
          setEmployeeMonthlyTotals(hydrated.employeeMonthlyTotals);
          setEmployeeYearlyTotal(hydrated.employeeYearlyTotal);
        }
      }
      
      // Hydrate calculation methodology from dynamic_field_values
      // If record has carbon_content/composition_of_carbon, it was carbon composition method
      // If record has ef_quantity, it was qty basis method
      const savedDynamicValues = editingEmission.dynamic_field_values || {};
      const savedCalculationMethodology = editingEmission.calculation_methodology
        || savedDynamicValues.calculation_methodology;
      const calculationMethodology = typeof savedCalculationMethodology === 'object'
        ? savedCalculationMethodology?.value
        : savedCalculationMethodology;
      const dfvKeys = Object.keys(savedDynamicValues);
      if (calculationMethodology) {
        setDecisionFieldValues(prev => ({ ...prev, calculation_methodology: calculationMethodology }));
      } else if (dfvKeys.includes('carbon_content') || dfvKeys.includes('composition_of_carbon')) {
        setDecisionFieldValues(prev => ({ ...prev, calculation_methodology: 'using_carbon_composition' }));
      } else if (dfvKeys.includes('ef_quantity')) {
        setDecisionFieldValues(prev => ({ ...prev, calculation_methodology: 'using_qty_basis_ef' }));
      }
      
      // Hydrate monthly data for monthly frequency
      if (editingEmission.frequency_type === 'monthly' || !editingEmission.frequency_type) {
        const dfv = editingEmission.dynamic_field_values || editingEmission.inputs || {};
        const monthKey = editingEmission.reporting_period?.split('-')[1]; // e.g., "01" from "2024-01"
        
        if (monthKey) {
          const monthData = {};
          Object.entries(dfv).forEach(([key, val]) => {
            if (val && typeof val === 'object' && 'value' in val) {
              monthData[key] = val.value;
              if (val.unit) {
                monthData[`${key}_unit`] = val.unit;
              }
            } else {
              monthData[key] = val;
            }
          });
          if (editingEmission.is_custom_fuel) {
            const savedQty = dfv.qty;
            const savedQtyUnit = typeof savedQty === 'object' ? savedQty.unit : '';
            monthData.custom_qty_unit = normalizeCustomFuelQuantityUnit(savedQtyUnit
              || resolveEmissionQuantity(editingEmission).unit
              || 'kg');
            if (monthData.density_unit) {
              monthData.density_unit = normalizeCustomFuelDensityUnit(monthData.density_unit);
            }
            if (monthData.custom_ef_unit) {
              monthData.custom_ef_unit = normalizeCustomFuelCompoundUnit(monthData.custom_ef_unit);
            }
            if (monthData.custom_cv_unit) {
              monthData.custom_cv_unit = normalizeCustomFuelCalorificValueUnit(monthData.custom_cv_unit);
            }
          }
          
          // Also include calculated values if they exist
          if (editingEmission.co2e_emissions !== undefined) {
            monthData.calculatedCO2e = editingEmission.co2e_emissions;
          }
          if (editingEmission.co2_emissions !== undefined) {
            monthData.calculatedCO2 = editingEmission.co2_emissions;
          }
          
          // Hydrate flight/airport data if present
          if (editingEmission.from_airport) {
            monthData.from_airport = editingEmission.from_airport;
          }
          if (editingEmission.to_airport) {
            monthData.to_airport = editingEmission.to_airport;
          }
          if (editingEmission.flight_distance) {
            const fd = editingEmission.flight_distance;
            monthData.km_travelled = typeof fd === 'object' ? fd.value : fd;
            monthData.flight_distance_method = typeof fd === 'object' ? fd.method : null;
            monthData.flight_distance_overridden = typeof fd === 'object' ? fd.overridden : false;
            monthData.flight_distance_manual = typeof fd === 'object' ? fd.method === 'MANUAL' : false;
          }
          
          setMonthlyData(prev => ({
            ...prev,
            [monthKey]: monthData
          }));
        }
      }
      
      // Hydrate yearly data for yearly frequency
      if (editingEmission.frequency_type === 'yearly') {
        const dfv = editingEmission.dynamic_field_values || editingEmission.inputs || {};
        const yearData = {};
        Object.entries(dfv).forEach(([key, val]) => {
          if (val && typeof val === 'object' && 'value' in val) {
            yearData[key] = val.value;
            if (val.unit) {
              yearData[`${key}_unit`] = val.unit;
            }
          } else {
            yearData[key] = val;
          }
        });
        if (editingEmission.is_custom_fuel) {
          const savedQty = dfv.qty;
          const savedQtyUnit = typeof savedQty === 'object' ? savedQty.unit : '';
          yearData.custom_qty_unit = normalizeCustomFuelQuantityUnit(savedQtyUnit
            || resolveEmissionQuantity(editingEmission).unit
            || 'kg');
          if (yearData.density_unit) {
            yearData.density_unit = normalizeCustomFuelDensityUnit(yearData.density_unit);
          }
          if (yearData.custom_ef_unit) {
            yearData.custom_ef_unit = normalizeCustomFuelCompoundUnit(yearData.custom_ef_unit);
          }
          if (yearData.custom_cv_unit) {
            yearData.custom_cv_unit = normalizeCustomFuelCalorificValueUnit(yearData.custom_cv_unit);
          }
        }
        
        // Also include calculated values
        if (editingEmission.co2e_emissions !== undefined) {
          yearData.calculatedCO2e = editingEmission.co2e_emissions;
        }
        if (editingEmission.co2_emissions !== undefined) {
          yearData.calculatedCO2 = editingEmission.co2_emissions;
        }
        
        // Hydrate flight/airport data if present
        if (editingEmission.from_airport) {
          yearData.from_airport = editingEmission.from_airport;
        }
        if (editingEmission.to_airport) {
          yearData.to_airport = editingEmission.to_airport;
        }
        if (editingEmission.flight_distance) {
          const fd = editingEmission.flight_distance;
          yearData.km_travelled = typeof fd === 'object' ? fd.value : fd;
          yearData.flight_distance_method = typeof fd === 'object' ? fd.method : null;
          yearData.flight_distance_overridden = typeof fd === 'object' ? fd.overridden : false;
          yearData.flight_distance_manual = typeof fd === 'object' ? fd.method === 'MANUAL' : false;
        }
        
        setYearlyData(prev => ({
          ...prev,
          ...yearData
        }));
      }
    }).catch(err => {
      console.error('Failed to load hydration utility:', err);
      // Fall back to basic hydration
      if (editingEmission.facility_id) setFacilityId(editingEmission.facility_id);
      if (editingEmission.scope) setScope(editingEmission.scope);
      if (editingEmission.category) setCategory(editingEmission.category);
    });
    
  }, [editingEmission?.id, fuelDatabase]);

  // ============================================================================
  // OCR PREFILL HELPERS
  // Isolated helper functions for OCR quantity prefill logic.
  // Structured for easy replacement with a generic mapping engine in the future.
  // ============================================================================
  
  /**
   * Find the primary activity/quantity field from formConfig fields.
   * This is typically the main numeric input field (e.g., energy_consumed, quantity, consumption).
   * @param {Array} fields - Array of field configurations from dynamicInputFields
   * @returns {Object|null} - The primary field config or null if not found
   */
  const findPrimaryActivityField = useCallback((fields) => {
    console.log('[OCR Debug] findPrimaryActivityField called with fields:', fields);
    
    if (!fields || fields.length === 0) {
      console.log('[OCR Debug] No fields provided');
      return null;
    }
    
    // Patterns to EXCLUDE (emission factors, override fields)
    const excludePatterns = [
      'ef_', 'emission_factor', 'ef_quantity', 'co2_ef', 'ch4_ef', 'n2o_ef',
      'gwp', 'density', 'cv', 'calorific'
    ];
    
    // Filter out emission factor and override fields
    const quantityFields = fields.filter(f => {
      const key = f.fieldKey?.toLowerCase() || '';
      return !excludePatterns.some(pattern => key.includes(pattern));
    });
    
    console.log('[OCR Debug] Filtered quantity fields:', quantityFields.map(f => f.fieldKey));
    
    // Priority order for identifying the primary quantity field
    const primaryFieldPatterns = [
      'qty',              // Main quantity field for Scope 1 (Stationary/Mobile Combustion)
      'qty_energy',       // Energy consumed for Scope 2
      'energy_consumed',
      'quantity',
      'activity_value',   // Scope 3 activity quantity
      'consumption',
      'amount',
      'volume',
      'mass',
      'fuel_consumed',
      'electricity_consumed',
      'gas_consumed'
    ];
    
    // Log all field keys for debugging (using fieldKey - camelCase)
    console.log('[OCR Debug] Available field keys:', fields.map(f => f.fieldKey));
    
    // First, try to find by fieldKey matching known patterns (in filtered fields)
    for (const pattern of primaryFieldPatterns) {
      const match = quantityFields.find(f => 
        f.fieldKey?.toLowerCase() === pattern ||
        f.fieldKey?.toLowerCase().includes(pattern)
      );
      if (match) {
        console.log('[OCR Debug] Found primary field by pattern:', pattern, '→', match.fieldKey);
        return match;
      }
    }
    
    // Fallback: find the first numeric field that has an associated unit field (from filtered)
    const numericField = quantityFields.find(f => 
      f.fieldType === 'number' && 
      fields.some(uf => uf.fieldKey === `${f.fieldKey}_unit`)
    );
    if (numericField) {
      console.log('[OCR Debug] Found primary field by numeric+unit fallback:', numericField.fieldKey);
      return numericField;
    }
    
    // Last resort: first numeric field from filtered list
    const firstNumeric = quantityFields.find(f => f.fieldType === 'number');
    if (firstNumeric) {
      console.log('[OCR Debug] Found primary field by first numeric fallback:', firstNumeric.fieldKey);
      return firstNumeric;
    }
    
    console.log('[OCR Debug] No primary field found!');
    return null;
  }, []);

  /**
   * Apply OCR quantity to the primary activity field in monthlyData.
   * @param {string} monthKey - The month key (e.g., '01', '06', '12')
   * @param {number} quantity - The OCR extracted quantity
   * @param {string} unit - The OCR extracted unit
   * @param {Object} primaryField - The primary field config from findPrimaryActivityField
   * @param {Function} setMonthlyData - State setter for monthlyData
   */
  const applyOcrQuantityToField = useCallback((monthKey, quantity, unit, primaryField, setMonthlyDataFn, availableUnits = []) => {
    console.log('[OCR Debug] applyOcrQuantityToField called:', { monthKey, quantity, unit, primaryField, availableUnits });
    
    if (!primaryField?.fieldKey || !monthKey) {
      console.log('[OCR Debug] Missing primaryField.fieldKey or monthKey, aborting');
      return;
    }
    
    const fieldKey = primaryField.fieldKey;
    const unitFieldKey = `${fieldKey}_unit`;
    
    // Normalize unit: find case-insensitive match in available units
    let normalizedUnit = unit || '';
    if (unit && availableUnits.length > 0) {
      const matchedUnit = availableUnits.find(u => u.toLowerCase() === unit.toLowerCase());
      if (matchedUnit) {
        normalizedUnit = matchedUnit;
        console.log(`[OCR Prefill] Normalized unit from "${unit}" to "${normalizedUnit}"`);
      }
    }
    
    console.log(`[OCR Prefill] Applying quantity ${quantity} ${normalizedUnit} to field: ${fieldKey}, unit field: ${unitFieldKey}`);
    
    setMonthlyDataFn(prev => {
      const updated = {
        ...prev,
        [monthKey]: {
          ...prev[monthKey],
          [fieldKey]: quantity,
          [unitFieldKey]: normalizedUnit
        }
      };
      console.log('[OCR Debug] Updated monthlyData:', updated);
      return updated;
    });
  }, []);

  // ============================================================================
  // OCR PREFILL HYDRATION
  // Auto-populate form fields from OCR Invoice Extractor workflow
  // ============================================================================
  
  // Store OCR month/quantity info for deferred application after formConfig loads
  const [ocrPendingQuantity, setOcrPendingQuantity] = useState(null);
  const [duplicateEvidencePrompt, setDuplicateEvidencePrompt] = useState(null);
  const duplicateEvidenceResolverRef = useRef(null);

  const resolveDuplicateEvidencePrompt = useCallback((shouldUpload) => {
    duplicateEvidenceResolverRef.current?.(shouldUpload);
    duplicateEvidenceResolverRef.current = null;
    setDuplicateEvidencePrompt(null);
  }, []);

  const confirmDuplicateEvidence = useCallback((file, sha256Hash, evidences, periodLabel) => {
    const duplicate = evidences.find((evidence) => (
      evidence?.filename === file.name
      && evidence?.sha256_hash
      && evidence.sha256_hash === sha256Hash
    ));
    if (!duplicate) return Promise.resolve(true);

    return new Promise((resolve) => {
      duplicateEvidenceResolverRef.current = resolve;
      setDuplicateEvidencePrompt({ filename: file.name, periodLabel });
    });
  }, []);
  
  // Phase 1: Set scope, category, fuel, and other metadata (runs immediately)
  useEffect(() => {
    if (!ocrPrefillData) return;
    
    console.log('[OCR Debug] Phase 1 - ocrPrefillData:', ocrPrefillData);
    console.log('[OCR Debug] Phase 1 - fuelDatabase length:', fuelDatabase?.length);
    
    // Set scope (scope1, scope2, scope3)
    if (ocrPrefillData.scope) {
      console.log('[OCR Debug] Setting scope:', ocrPrefillData.scope);
      setScope(ocrPrefillData.scope);
    }
    
    // Set category
    if (ocrPrefillData.category) {
      console.log('[OCR Debug] Setting category:', ocrPrefillData.category);
      setCategory(ocrPrefillData.category);
    }

    if (ocrPrefillData.facility_id && facilities.some((facility) => facility.id === ocrPrefillData.facility_id)) {
      setFacilityId(ocrPrefillData.facility_id);
    }

    if (ocrPrefillData.scope === 'scope3') {
      if (ocrPrefillData.calculation_method_scope3) setScope3Method(ocrPrefillData.calculation_method_scope3);
      if (ocrPrefillData.scope3_activity_type) setScope3ActivityType(ocrPrefillData.scope3_activity_type);
      if (ocrPrefillData.scope3_subcategory) setScope3Subcategory(ocrPrefillData.scope3_subcategory);
      if (ocrPrefillData.scope3_ef_id) setScope3ActivityId(ocrPrefillData.scope3_ef_id);
      if (ocrPrefillData.supplier_name) setSupplierName(ocrPrefillData.supplier_name);
    }
    
    // Set fuel type by looking up fuelId from fuelDatabase
    // For Scope 2 electricity, look for subcategory match (e.g., "Non-Renewable Electricity")
    if (fuelDatabase?.length > 0) {
      const fuelNameLower = (ocrPrefillData.fuel_name || '').toLowerCase();
      const subcategoryLower = (ocrPrefillData.subcategory || '').toLowerCase();
      const categoryLower = (ocrPrefillData.category || '').toLowerCase();
      
      console.log('[OCR Debug] Looking for fuel match:', { fuelNameLower, subcategoryLower, categoryLower });
      // Log first 10 fuels to understand the structure
      console.log('[OCR Debug] Sample fuels (first 10):', fuelDatabase.slice(0, 10).map(f => ({ 
        id: f.id, 
        name: f.name, 
        activity: f.activity,
        fuel_name: f.fuel_name,
        category: f.category,
        subcategory: f.subcategory
      })));
      
      let matchedFuel = ocrPrefillData.fuel_id
        ? fuelDatabase.find((fuel) => fuel.id === ocrPrefillData.fuel_id)
        : null;
      
      // Helper function to check if any field matches the search term
      const fuelMatches = (fuel, searchTerm) => {
        if (!searchTerm) return false;
        const searchLower = searchTerm.toLowerCase();
        
        // Check all possible name fields
        const fieldsToCheck = [
          fuel.name,
          fuel.activity,
          fuel.fuel_name,
          fuel.subcategory,
          fuel.label,
          fuel.display_name
        ];
        
        for (const field of fieldsToCheck) {
          if (!field) continue;
          const fieldLower = field.toLowerCase();
          // Exact match
          if (fieldLower === searchLower) return true;
          // Contains match
          if (fieldLower.includes(searchLower) || searchLower.includes(fieldLower)) return true;
        }
        return false;
      };
      
      // For Scope 2, try to match by subcategory first (e.g., "Non-Renewable Electricity")
      if (!matchedFuel && subcategoryLower) {
        matchedFuel = fuelDatabase.find(f => fuelMatches(f, subcategoryLower));
        if (matchedFuel) {
          console.log('[OCR Debug] Matched fuel by subcategory:', matchedFuel.name || matchedFuel.activity);
        }
      }
      
      // Try matching by fuel_name (e.g., "Electricity")
      if (!matchedFuel && fuelNameLower) {
        matchedFuel = fuelDatabase.find(f => fuelMatches(f, fuelNameLower));
        if (matchedFuel) {
          console.log('[OCR Debug] Matched fuel by fuel_name:', matchedFuel.name || matchedFuel.activity);
        }
      }
      
      // Try matching common variations for electricity
      if (!matchedFuel && (fuelNameLower.includes('electric') || subcategoryLower.includes('electric'))) {
        const electricityVariations = [
          'non-renewable electricity',
          'non renewable electricity', 
          'nonrenewable electricity',
          'grid electricity',
          'purchased electricity',
          'electricity - non-renewable',
          'electricity (non-renewable)',
          'electricity'
        ];
        
        for (const variation of electricityVariations) {
          matchedFuel = fuelDatabase.find(f => fuelMatches(f, variation));
          if (matchedFuel) {
            console.log('[OCR Debug] Matched fuel by electricity variation:', variation, '→', matchedFuel.name || matchedFuel.activity);
            break;
          }
        }
      }
      
      if (matchedFuel?.id) {
        console.log('[OCR Prefill] Setting fuelId:', matchedFuel.id, matchedFuel.name || matchedFuel.activity);
        setFuelId(matchedFuel.id);
      } else {
        console.log('[OCR Debug] No fuel match found. Available fuel names:', 
          fuelDatabase.slice(0, 20).map(f => f.name || f.activity || f.fuel_name).filter(Boolean)
        );
      }
    }
    
    // Set responsible person (current user from OCR accept)
    if (ocrPrefillData.responsible_person) {
      setResponsiblePerson(ocrPrefillData.responsible_person);
    }
    
    // Set record source (invoice info)
    if (ocrPrefillData.source_of_information) {
      setRecordSource(ocrPrefillData.source_of_information);
    }
    
    // Store quantity info for Phase 2 (after formConfig loads)
    const periodMatch = String(
      ocrPrefillData.reporting_period || ocrPrefillData.billing_period?.start_date || '',
    ).match(/^(\d{4})-(0[1-9]|1[0-2])/);
    const hasOcrInput = [ocrPrefillData.quantity, ocrPrefillData.cost, ocrPrefillData.distance_km]
      .some((value) => value !== undefined && value !== null && value !== '');
    if (hasOcrInput && periodMatch) {
      const [, extractedYear, extractedMonth] = periodMatch;
      const monthKey = extractedMonth;
      const calendarYear = Number(extractedYear);
      const year = reportingYearType === 'financial' && Number(extractedMonth) <= 3
        ? calendarYear - 1
        : calendarYear;
      
      console.log('[OCR Debug] Phase 1 - Setting up pending quantity:', { monthKey, year, quantity: ocrPrefillData.quantity });
      
      // Set reporting year immediately
      setReportingYear(String(year));
      
      // Expand the month immediately
      setExpandedMonths(prev => {
        if (Array.isArray(prev) && !prev.includes(monthKey)) {
          return [...prev, monthKey];
        }
        return prev;
      });
      
      // Store for Phase 2
      setOcrPendingQuantity({
        monthKey,
        quantity: ocrPrefillData.quantity,
        unit: ocrPrefillData.unit || '',
        cost: ocrPrefillData.cost,
        currency: ocrPrefillData.currency || '',
        distance: ocrPrefillData.distance_km,
        method: ocrPrefillData.calculation_method_scope3 || '',
      });
    }

    // Process Name and Description are intentionally left empty for review.
  }, [facilities, fuelDatabase, ocrPrefillData, reportingYearType]);

  // Sync decisionFieldValues + custom-activity auto-enable now live inside
  // useEmissionFormState (F2 integration). The corresponding inline useEffects
  // were removed here.

  const ghgCapabilityContext = useMemo(() => resolveGhgFormContext({
    scope,
    biogenicScopeSelection,
    categoryName: category,
    categories: dynamicCategories,
    scopes: dynamicScopes,
  }), [
    scope,
    biogenicScopeSelection,
    category,
    dynamicCategories,
    dynamicScopes,
  ]);
  const ghgFormArchitecture = useMemo(() => resolveGhgFormArchitecture({
    standardConfig: formConfig,
    organizationOverrides: organizationGhgOverrides,
    formContext: ghgCapabilityContext,
    biogenicScopeSelection,
  }), [formConfig, organizationGhgOverrides, ghgCapabilityContext, biogenicScopeSelection]);
  const resolvedCapabilities = ghgFormArchitecture.capabilities;
  const resolvedGhgFieldOptions = ghgFormArchitecture.resolvedFieldOptions;
  const hasSubcategoryCapability = resolvedCapabilities.subcategory;
  const scope3PresentationOptions = useMemo(() => resolveGhgScope3Options({
    scope,
    biogenicScopeSelection,
    category,
    scope3Method,
    scope3EFData,
    capabilities: resolvedCapabilities,
    requiresSubcategory: scope === 'scope3' && Boolean(category) && hasSubcategoryCapability,
    fieldOptions: resolvedGhgFieldOptions,
    configLabels: configLabels?.subcategories || {},
  }), [
    scope, biogenicScopeSelection, category, scope3Method, scope3EFData,
    resolvedCapabilities, hasSubcategoryCapability, resolvedGhgFieldOptions, configLabels?.subcategories,
  ]);

  // Filter Scope 3 activities based on category, method, industry sector, and year
  // Note: selectedFacility is defined below after fuelDatabase useMemo
  const filteredScope3Activities = useMemo(() => {
    // Handle both regular scope3 and biogenic with scope3 selection
    const isScope3 = scope === 'scope3';
    const isBiogenicScope3 = scope === 'biogenic' && biogenicScopeSelection === 'scope3';
    
    if (!isScope3 && !isBiogenicScope3) return [];
    if (!scope3EFData.length) return [];
    
    // Get facility for sector filtering
    const facility = facilities.find(f => f.id === facilityId);
    
    // For biogenic, filter by sub_scope='biogenic'
    // For regular scope3, EXCLUDE biogenic data
    let filtered = isBiogenicScope3 
      ? scope3EFData.filter(ef => ef.sub_scope === 'biogenic')
      : scope3EFData.filter(ef => ef.sub_scope !== 'biogenic'); // Exclude biogenic from regular scope3
    
    // For subcategory-based categories (C8, C10, C11, C13, C14), handle specially
    const catLower = category?.toLowerCase() || '';
    const isSubcategoryCategory = hasSubcategoryCapability;
    
    // For BIOGENIC scope3, skip subcategory handling - just filter by category directly
    // Biogenic C8/C10/C11/C13/C14 should work like C3 (direct activity selection)
    if (isBiogenicScope3 && isSubcategoryCategory) {
      // Filter biogenic activities by category
      filtered = filtered.filter(ef => 
        ef.category?.toLowerCase() === catLower
      );
      // Continue to standard filtering below (method, etc.)
    }
    // For REGULAR scope3 with subcategory categories, require subcategory selection
    else if (isScope3 && isSubcategoryCategory && scope3Subcategory) {
      // For fugitive_emissions, return data from fugitiveEmissionsData instead
      if (scope3Subcategory === 'fugitive_emissions') {
        return fugitiveEmissionsData.map(f => ({
          id: f.id,
          activity: f.activity,
          emission_factor: f.emission_factor,
          unit: f.unit || 'kgCO2e/kg',
          source: f.source || 'Fugitive Emissions',
          method: scope3Method,
          category: category,
          allowed_units: f.allowed_units || ['kg', 'g', 't'],
          default_unit: f.default_unit || 'kg'
        }));
      }
      
      // For stationary_combustion, mobile_combustion, and energy, filter from scope3_ef
      if (scope3Subcategory === 'stationary_combustion' || 
          scope3Subcategory === 'mobile_combustion' || 
          scope3Subcategory === 'energy' ||
          scope3Subcategory === 'electricity') {  // Support legacy 'electricity' value
        filtered = filtered.filter(ef => 
          ef.category?.toLowerCase() === catLower
        );
        
        // Filter by subcategory field if it exists on the entry
        // For energy: ONLY show entries with exact match (no fallback to null/empty)
        // For stationary/mobile: If entry has no subcategory defined, show in both
        const energySubcategory = scope3Subcategory === 'electricity' ? 'energy' : scope3Subcategory;
        filtered = filtered.filter(ef => {
          if (energySubcategory === 'energy') {
            // Strict matching - only show entries explicitly marked as energy
            // Also support legacy 'electricity' entries
            if (Array.isArray(ef.subcategory)) {
              return ef.subcategory.includes('energy') || ef.subcategory.includes('electricity');
            }
            return ef.subcategory === 'energy' || ef.subcategory === 'electricity';
          }
          
          // For stationary/mobile: fallback to null/empty subcategory
          if (!ef.subcategory || ef.subcategory.length === 0) {
            // No subcategory defined - show in both stationary and mobile
            return true;
          }
          // Has subcategory defined - check if it matches
          if (Array.isArray(ef.subcategory)) {
            return ef.subcategory.includes(scope3Subcategory);
          }
          return ef.subcategory === scope3Subcategory;
        });
        
        // Filter by method
        if (scope3Method && scope3Method !== 'supplier_basis') {
          filtered = filtered.filter(ef => ef.method === scope3Method);
        }
        
        return selectClosestScope3Factors(filtered, reportingYear);
      }
    }
    
    // Standard filtering for non-subcategory categories
    // Filter by category
    if (category) {
      filtered = filtered.filter(ef => 
        ef.category?.toLowerCase() === category.toLowerCase()
      );
    }
    
    // Filter by method - for supplier_basis, show ALL activities for the category
    // For spend_basis/activity_basis, filter by specific method
    if (scope3Method && scope3Method !== 'supplier_basis') {
      filtered = filtered.filter(ef => ef.method === scope3Method);
    }
    
    // Filter by activity_type for categories that expose an Activity Type selector.
    if (scope3ActivityType) {
      filtered = filtered.filter((ef) => ef.activity_type === scope3ActivityType);
    }
    
    // Filter by industry sector (if facility has one)
    if (facility?.sector) {
      filtered = filtered.filter(ef => {
        // Check if EF has industry_sectors array
        if (ef.industry_sectors && ef.industry_sectors.length > 0) {
          return ef.industry_sectors.some(s => 
            s.toLowerCase() === facility.sector.toLowerCase()
          );
        }
        // If no industry filter on EF, show it (backwards compatibility)
        return true;
      });
    }
    
    return selectClosestScope3Factors(filtered, reportingYear);
  }, [scope, scope3EFData, category, scope3Method, scope3ActivityType, scope3Subcategory, fugitiveEmissionsData, facilities, facilityId, biogenicScopeSelection, hasSubcategoryCapability, reportingYear]);

  useEffect(() => {
    if (!scope3ActivityId || filteredScope3Activities.some((activity) => activity.id === scope3ActivityId)) return;
    const previouslySelected = scope3EFData.find((activity) => activity.id === scope3ActivityId);
    const replacement = filteredScope3Activities.find((activity) => (
      previouslySelected?.activity
      && activity.activity?.toLowerCase() === previouslySelected.activity.toLowerCase()
    ));
    if (replacement) setScope3ActivityId(replacement.id);
  }, [filteredScope3Activities, scope3ActivityId, scope3EFData, setScope3ActivityId]);

  // Get available activity types for categories configured with that capability.
  const availableScope3ActivityTypes = scope3PresentationOptions.activityTypes;
  
  // Check if current category requires Asset Name
  const requiresAssetName = useMemo(() => {
    return scope === 'scope3' && Boolean(category) && resolvedCapabilities.assetName;
  }, [scope, category, resolvedCapabilities]);
  
  // Check if current category is C7 (Employee Commuting) - supports multi-employee input
  const isC7EmployeeCommuting = useMemo(() => {
    return scope === 'scope3' && Boolean(category) && resolvedCapabilities.multiEmployee;
  }, [scope, category, resolvedCapabilities]);

  // C6 Create supports multiple independent trips in one reporting period.
  // Existing records continue through the standard single-record Edit flow.
  const isC6MultiTrip = useMemo(() => (
    !editingEmission && !ocrPrefillData && scope === 'scope3' && /^c6\b/i.test(category || '')
  ), [category, editingEmission, ocrPrefillData, scope]);
  
  // Check if current category requires subcategory
  // Note: Biogenic Scope 3 does NOT require subcategory - it uses direct activity selection like C3
  const requiresSubcategory = useMemo(() => {
    // Only regular Scope 3 requires subcategory for C8/C10/C11/C13/C14
    // Biogenic Scope 3 skips subcategory selection
    const isBiogenicScope3 = scope === 'biogenic' && biogenicScopeSelection === 'scope3';
    if (scope !== 'scope3' || isBiogenicScope3 || !category) return false;
    return resolvedCapabilities.subcategory;
  }, [scope, category, biogenicScopeSelection, resolvedCapabilities]);

  // Reset employee data when category changes away from C7
  useEffect(() => {
    if (!isC7EmployeeCommuting) {
      setEmployees([]);
      setEmployeeMonthlyTotals({});
      setEmployeeYearlyTotal({});
    }
  }, [isC7EmployeeCommuting]);

  // Clear employee calculations when activity changes for C7
  // This forces users to recalculate with the new activity's emission factor
  useEffect(() => {
    if (isC7EmployeeCommuting && employees.length > 0) {
      // Clear calculated emissions from all employees while preserving input data
      setEmployees(prevEmployees => prevEmployees.map(emp => ({
        ...emp,
        // Clear monthly calculations
        monthly_data: Object.fromEntries(
          Object.entries(emp.monthly_data || {}).map(([month, data]) => [
            month,
            {
              ...data,
              emissions: null, // Clear the emissions object
              calculation_details: null,
            }
          ])
        ),
        // Clear yearly calculations
        yearly_data: emp.yearly_data ? {
          ...emp.yearly_data,
          emissions: null, // Clear the emissions object
          calculation_details: null,
        } : null,
      })));
      setEmployeeMonthlyTotals({});
      setEmployeeYearlyTotal({});
      setC7FormulaId(null);
      setC7FormulaName('');
    }
  }, [scope3ActivityId, scope3ActivityType]); // Reset when activity changes

  // Reset asset name when category changes away from C8/C13/C14/C15
  useEffect(() => {
    if (!requiresAssetName) {
      setAssetName('');
    }
  }, [requiresAssetName]);

  // Check if current category shows From/To Location fields.
  const showsLocationFields = useMemo(() => {
    return !isC6MultiTrip && scope === 'scope3' && Boolean(category) && resolvedCapabilities.journeyLocations;
  }, [isC6MultiTrip, scope, category, resolvedCapabilities]);

  // Reset location fields when category changes away from C4/C6/C9
  useEffect(() => {
    if (!showsLocationFields) {
      setFromLocation('');
      setToLocation('');
    }
  }, [showsLocationFields]);

  // Get available subcategories for C8/C10/C11/C13/C14
  const availableSubcategories = scope3PresentationOptions.subcategories;

  // Get available methods for selected category from Scope 3 EF
  // Always include supplier_basis as an option (except for biogenic)
  const availableScope3Methods = scope3PresentationOptions.methods;
  const handleCategoryChange = useCallback((nextCategory) => {
    if (nextCategory === category) return;

    const supportedMethods = resolveScope3MethodsForCategory({
      scope,
      biogenicScopeSelection,
      category: nextCategory,
      scope3EFData,
    });
    const nextScope3Method = supportedMethods.includes(scope3Method) ? scope3Method : '';

    setCategory(nextCategory);
    setScope3Method(nextScope3Method);
    setSpendCurrencyConversionMethod('standard');
    setAllocationMethod('');
    setFuelId('');
    setUseCustomFuel(false);
    setCustomFuelName('');
    setCustomEmissionFactor('');
    setCustomEmissionFactorUnit('');
    setCustomSource('');
    setScope3ActivityType('');
    setScope3Subcategory('');
    setTypeOfProduct('');
    setScope3ActivityId('');
    setUseCustomActivity(false);
    setScope3CustomActivity('');
    setFuelSearchTerm('');
    setAssetName('');
    setFromLocation('');
    setToLocation('');
    setSupplierName('');
    setSupplierCode('');
    setEmployees([]);
    setEmployeeMonthlyTotals({});
    setEmployeeYearlyTotal({});
    setC7FormulaId(null);
    setC7FormulaName('');
    setC6Trips({ monthly: {}, yearly: [] });
    setDecisionFieldValues(nextScope3Method ? {
      calculation_method_scope3: nextScope3Method,
      ...(nextScope3Method === 'spend_basis' && {
        spend_currency_conversion_method: 'standard',
      }),
    } : {});
    setMonthlyData({});
    setYearlyData({});
    setCalcEngineResult(null);
    setYearlyCalcResult(null);
    setMatchedFormulaId(null);
    setFormConfig(null);
  }, [
    biogenicScopeSelection,
    category,
    scope,
    scope3EFData,
    scope3Method,
    setAssetName,
    setC7FormulaId,
    setC7FormulaName,
    setC6Trips,
    setCalcEngineResult,
    setCategory,
    setCustomEmissionFactor,
    setCustomEmissionFactorUnit,
    setCustomFuelName,
    setCustomSource,
    setDecisionFieldValues,
    setEmployeeMonthlyTotals,
    setEmployeeYearlyTotal,
    setEmployees,
    setFormConfig,
    setFromLocation,
    setFuelId,
    setFuelSearchTerm,
    setMatchedFormulaId,
    setMonthlyData,
    setScope3ActivityId,
    setScope3ActivityType,
    setScope3CustomActivity,
    setScope3Method,
    setAllocationMethod,
    setScope3Subcategory,
    setSpendCurrencyConversionMethod,
    setSupplierCode,
    setSupplierName,
    setToLocation,
    setTypeOfProduct,
    setUseCustomActivity,
    setUseCustomFuel,
    setYearlyCalcResult,
    setYearlyData,
  ]);

  const handleC8AllocationMethodChange = useCallback((nextAllocationMethod) => {
    setAllocationMethod(nextAllocationMethod);
    setScope3Subcategory('');
    setScope3ActivityId('');
    setFuelSearchTerm('');
    setCalcEngineResult(null);
    setYearlyCalcResult(null);
    setMatchedFormulaId(null);
  }, [
    setAllocationMethod,
    setScope3Subcategory,
    setScope3ActivityId,
    setFuelSearchTerm,
    setCalcEngineResult,
    setYearlyCalcResult,
    setMatchedFormulaId,
  ]);

  const emissionFactorUnits = resolvedGhgFieldOptions[GHG_FIELD_OPTION_KEYS.EMISSION_FACTOR_UNIT] || [];
  const customFuelUnits = resolvedGhgFieldOptions[GHG_FIELD_OPTION_KEYS.CUSTOM_FUEL_EMISSION_FACTOR_UNIT] || [];

  // Get available EF units based on scope
  const getAvailableEFUnits = (currentScope, isCustomFuel = false) => {
    if (isCustomFuel) {
      return customFuelUnits;
    }
    return emissionFactorUnits.filter(u => !u.forScope || u.forScope.includes(currentScope));
  };

  // Get quantity unit based on emission factor unit for custom fuels
  const getQuantityUnitFromEFUnit = (efUnit) => {
    // Check custom fuel units first
    const customMapping = customFuelUnits.find(u => u.value === efUnit);
    if (customMapping) return customMapping.quantityUnit;
    // Fallback to standard units
    const mapping = emissionFactorUnits.find(u => u.value === efUnit);
    return mapping?.quantityUnit || 'kg';
  };

  const customFugitiveQuantityUnits = useMemo(() => {
    if (!useCustomFuel || !category?.toLowerCase().includes('fugitive')) return [];
    return getCategoryFuelAllowedUnits({
      fuelDatabase,
      scope: resolveEffectiveScopeCode(scope, biogenicScopeSelection),
      categoryName: category,
    });
  }, [useCustomFuel, category, fuelDatabase, scope, biogenicScopeSelection]);

  // Resolve the quantity unit for custom fuels based on selected methodology
  const customFuelQtyUnit = useMemo(() => {
    if (!useCustomFuel) return null;
    if (category?.toLowerCase().includes('fugitive')) {
      return decisionFieldValues._customQtyUnit || customFugitiveQuantityUnits[0] || '';
    }
    const calcMethod = decisionFieldValues.calculation_methodology || 'using_heat_basis_ncv';
    if (calcMethod === 'using_qty_basis_ef') {
      return getQuantityUnitFromEFUnit(customEmissionFactorUnit);
    }
    // Heat Basis and Carbon Composition: qty unit selected independently
    return decisionFieldValues._customQtyUnit || 'kg';
  }, [useCustomFuel, category, decisionFieldValues, customEmissionFactorUnit, customFugitiveQuantityUnits, getQuantityUnitFromEFUnit]);

  // Step 2 + Step 3 form state moved to useEmissionFormState (F2 integration).
  // The hook also owns the reporting-year-type org-pref sync useEffect and the
  // editingEmission frequency_type/yearlyData hydration useEffect.

  // Helper function to update yearly data with validation
  const updateYearlyData = useCallback((field, value) => {
    setYearlyData(prev => ({ ...prev, [field]: value }));
  }, [setYearlyData]);

  // Get active months based on reporting year type
  const activeMonths = useMemo(() => {
    return reportingYearType === 'financial' ? FINANCIAL_YEAR_MONTHS : CALENDAR_YEAR_MONTHS;
  }, [reportingYearType]);

  // Get the actual year for a month based on reporting type
  // For financial year: Apr-Dec use selected year, Jan-Mar use selected year + 1
  // Stable callback: spend-currency memos depend on it, so a new fn identity
  // every render would defeat their memoization and loop the defaults fetch.
  const getActualYearForMonth = useCallback((monthKey) => {
    if (reportingYearType === 'financial') {
      const monthNum = parseInt(monthKey);
      if (monthNum >= 1 && monthNum <= 3) {
        return (parseInt(reportingYear) + 1).toString();
      }
    }
    return reportingYear;
  }, [reportingYearType, reportingYear]);

  // Step 4: Notes — moved to useEmissionFormState (F2 integration).
  
  // Track form dirty state for unsaved changes protection (#19)
  useEffect(() => {
    // Only trigger after user interaction (not initial load)
    if (facilityId || category || fuelId || notes) {
      if (typeof onFormChange === 'function') {
        onFormChange();
      }
    }
  }, [facilityId, category, fuelId, notes, scope3Method, spendCurrencyConversionMethod, scope3ActivityType, employees.length, onFormChange]);
  
  // Scope 3 specific optional fields — moved to useEmissionFormState (F2 integration).


  // Get selected fuel data
  const selectedFuel = useMemo(() => {
    return fuelDatabase.find(f => f.id === fuelId);
  }, [fuelDatabase, fuelId]);

  // Get selected facility
  const selectedFacility = useMemo(() => {
    return facilities.find(f => f.id === facilityId);
  }, [facilities, facilityId]);

  // Get categories for selected scope — prefer SuperAdmin-managed categories,
  // fall back to those inferred from the fuel database for compatibility.
  const standardCategoriesForScope = useMemo(() => {
    // For biogenic with scope3 selected, return biogenic categories
    if (scope === 'biogenic' && biogenicScopeSelection === 'scope3') {
      return biogenicCategories.sort((a, b) => {
        const numA = parseInt(a.match(/C(\d+)/)?.[1] || '999');
        const numB = parseInt(b.match(/C(\d+)/)?.[1] || '999');
        return numA - numB;
      });
    }
    
    // For biogenic with scope1 selected, use 'biogenic' scope from fuel_database
    // fuel_database has scope='biogenic' or 'Biogenic' for biogenic fuels
    const effectiveScopeForCategories = resolveEffectiveScopeCode(
      scope,
      biogenicScopeSelection,
    );
    
    const cats = new Set();

    // Primary source: SuperAdmin dynamic categories
    (dynamicCategories || [])
      .filter(c => c.scope_code === effectiveScopeForCategories && c.is_active !== false)
      .forEach(c => cats.add(c.name));

    // Fallback/union: categories already present in the fuel database
    // Handle both 'biogenic' and 'Biogenic' case variations
    const filtered = fuelDatabase.filter(f => 
      f.scope === effectiveScopeForCategories || 
      f.scope?.toLowerCase() === effectiveScopeForCategories.toLowerCase()
    );
    filtered.forEach(f => {
      if (f.categories?.length > 0) {
        f.categories.forEach(c => cats.add(c));
      } else if (f.category) {
        cats.add(f.category);
      }
    });

    let result = Array.from(cats);
    
    // For Scope 3, sort by category number (C1, C2, ... C15)
    if (effectiveScopeForCategories === 'scope3') {
      result.sort((a, b) => {
        const numA = parseInt(a.match(/C(\d+)/)?.[1] || '999');
        const numB = parseInt(b.match(/C(\d+)/)?.[1] || '999');
        return numA - numB;
      });
    } else {
      result.sort();
    }
    
    return result;
  }, [fuelDatabase, scope, dynamicCategories, biogenicScopeSelection, biogenicCategories]);

  const categoriesForScope = useMemo(() => resolveGhgCategoryOptions({
    standardCategories: supplierContext
      ? filterSupplierVisibleCategories(standardCategoriesForScope, resolveEffectiveScopeCode(scope, biogenicScopeSelection), supplierGhgConfig)
      : standardCategoriesForScope,
    scopeCode: resolveEffectiveScopeCode(scope, biogenicScopeSelection),
    categoryDefinitions: dynamicCategories,
    organizationOverrides: organizationGhgOverrides,
  }), [standardCategoriesForScope, scope, biogenicScopeSelection, dynamicCategories, organizationGhgOverrides, supplierContext, supplierGhgConfig]);

  // ============================================================================
  // Dynamic Form Config - fields come from ce_input_field_mappings.
  // Derivation is delegated to the shared, pure GHG config layer
  // (modules/ghg/config) so Create, and later Edit, use one implementation.
  //   resolved config + explicit context -> fields + resolved formula
  // ============================================================================
  const resolvedGhgConfig = ghgFormArchitecture.resolvedConfig;

  const ghgFormContext = useMemo(
    () =>
      resolveGhgFormContext({
        scope,
        biogenicScopeSelection,
        categoryName: category,
        categories: dynamicCategories,
        scopes: dynamicScopes,
        scope3Method,
        spendCurrencyConversionMethod,
        allocationMethod,
        scope3ActivityType,
        scope3Subcategory,
        typeOfProduct,
        decisionFieldValues,
        useCustomFuel,
        selectedFuel,
      }),
    [
      scope,
      biogenicScopeSelection,
      category,
      dynamicCategories,
      dynamicScopes,
      scope3Method,
      spendCurrencyConversionMethod,
      allocationMethod,
      scope3ActivityType,
      scope3Subcategory,
      typeOfProduct,
      decisionFieldValues,
      useCustomFuel,
      selectedFuel,
    ],
  );

  const dynamicInputFieldsResult = useMemo(
    () => deriveGhgFields({ formConfig: resolvedGhgConfig, context: ghgFormContext }),
    [resolvedGhgConfig, ghgFormContext],
  );
  
  // Extract fields and formula ID from the memoized result
  const dynamicInputFields = dynamicInputFieldsResult?.fields || [];
  const currentFormulaId = dynamicInputFieldsResult?.formulaId || null;
  const supplierBasisUnitResetRef = useRef(false);
  useEffect(() => {
    if (scope3Method !== 'supplier_basis') {
      supplierBasisUnitResetRef.current = false;
      return;
    }
    if (editingEmission || supplierBasisUnitResetRef.current || dynamicInputFields.length === 0) return;

    const unitKeys = dynamicInputFields
      .filter((field) => !field.variable?.endsWith('_unit'))
      .map((field) => `${field.variable}_unit`);
    const clearUnits = (data = {}) => {
      const next = { ...data };
      unitKeys.forEach((key) => delete next[key]);
      return next;
    };

    setMonthlyData((previousMonths) => Object.fromEntries(
      Object.entries(previousMonths).map(([monthKey, data]) => [monthKey, clearUnits(data)]),
    ));
    setYearlyData((previous) => clearUnits(previous));
    setC6Trips((previous) => ({
      yearly: (previous.yearly || []).map(clearUnits),
      monthly: Object.fromEntries(
        Object.entries(previous.monthly || {}).map(([monthKey, trips]) => [
          monthKey,
          (trips || []).map(clearUnits),
        ]),
      ),
    }));
    supplierBasisUnitResetRef.current = true;
  }, [dynamicInputFields, editingEmission, scope3Method, setC6Trips, setMonthlyData, setYearlyData]);
  const spendValueField = dynamicInputFields.find((field) => field.variable === 'spent_value');
  const spendSourceCurrency = yearlyData.spent_value_unit
    || Object.values(monthlyData).find((data) => data?.spent_value_unit)?.spent_value_unit
    || spendValueField?.expectedUnit
    || spendValueField?.allowedUnits?.[0]
    || '';
  const spendCurrencyFallback = spendValueField?.expectedUnit
    || spendValueField?.allowedUnits?.[0]
    || '';
  const spendCurrencyByPeriod = useMemo(() => {
    if (scope3Method !== 'spend_basis') return EMPTY_SPEND_CURRENCY_MAP;
    const map = {};
    if (frequencyType === 'yearly') {
      const yearlyPeriod = reportingYearType === 'financial'
        ? `FY ${reportingYear}-${String(Number(reportingYear) + 1).slice(-2)}`
        : `CY${reportingYear}`;
      const cur = yearlyData.spent_value_unit || spendCurrencyFallback;
      if (cur) map[yearlyPeriod] = cur;
      return map;
    }
    (activeMonths || []).forEach((m) => {
      const monthKey = m.key || m;
      const actualYear = getActualYearForMonth(monthKey);
      const period = `${actualYear}-${monthKey}`;
      const cur = (monthlyData?.[monthKey]?.spent_value_unit) || spendCurrencyFallback;
      if (cur) map[period] = cur;
    });
    return map;
  }, [scope3Method, frequencyType, reportingYearType, reportingYear, activeMonths, monthlyData, yearlyData, spendCurrencyFallback, getActualYearForMonth]);
  const spendCurrencyDefaults = useSpendCurrencyDefaults({
    enabled: scope3Method === 'spend_basis',
    sourceCurrency: spendSourceCurrency,
    currencyByPeriod: spendCurrencyByPeriod,
    conversionMethod: spendCurrencyConversionMethod,
    reportingYearType,
    reportingYear,
    frequencyType,
    activeMonths,
    getAuthHeader,
  });

  // Clear stale spend conversion rates per-row when that row's source currency
  // changes. The previous aggregate scalar (first monthlyData match) never
  // flipped when only one of 12 monthly rows changed, so April INR 93.32 stuck
  // after switching that row to USD. Track effective currency per row/year/trip
  // and strip only the rows whose currency actually changed.
  const SPEND_RATE_STRIP_KEYS = ['exchange_rate', 'ppp', 'inflation_rate', 'exchange_rate_unit', 'ppp_unit', 'inflation_rate_unit', 'override_exchange_rate', 'override_ppp', 'override_inflation_rate'];
  const prevSpendRowCurrencyRef = useRef({});
  const prevSpendTripCurrencyRef = useRef({});
  useEffect(() => {
    if (scope3Method !== 'spend_basis') {
      return;
    }
    const fallback = spendCurrencyFallback;
    const stripRow = (row = {}) => {
      const next = { ...row };
      SPEND_RATE_STRIP_KEYS.forEach((key) => { delete next[key]; });
      return next;
    };
    const hasStaleRate = (row = {}) => SPEND_RATE_STRIP_KEYS.some((key) => Object.prototype.hasOwnProperty.call(row, key));

    const prevRowMap = prevSpendRowCurrencyRef.current || {};
    const nextRowMap = {};
    const changedMonths = [];
    Object.entries(monthlyData || {}).forEach(([monthKey, row]) => {
      const cur = row?.spent_value_unit || fallback || '';
      const period = `${getActualYearForMonth(monthKey)}-${monthKey}`;
      nextRowMap[monthKey] = cur;
      nextRowMap[period] = cur;
      const prev = prevRowMap[monthKey] ?? prevRowMap[period];
      if (prev === undefined || prev === cur || !cur) return;
      if (hasStaleRate(row)) changedMonths.push(monthKey);
    });
    const yearlyPeriod = reportingYearType === 'financial'
      ? `FY ${reportingYear}-${String(Number(reportingYear) + 1).slice(-2)}`
      : `CY${reportingYear}`;
    const curYearly = yearlyData?.spent_value_unit || fallback || '';
    nextRowMap.__yearly = curYearly;
    nextRowMap[yearlyPeriod] = curYearly;
    const prevYearly = prevRowMap.__yearly ?? prevRowMap[yearlyPeriod];
    const yearlyChanged = prevYearly !== undefined && curYearly && prevYearly !== curYearly && hasStaleRate(yearlyData);
    prevSpendRowCurrencyRef.current = nextRowMap;

    const prevTripMap = prevSpendTripCurrencyRef.current || {};
    const nextTripMap = {};
    const changedTripIds = new Set();
    const collectTrips = (trips = []) => {
      (trips || []).forEach((trip) => {
        if (!trip?.id) return;
        const cur = trip?.spent_value_unit || fallback || '';
        nextTripMap[trip.id] = cur;
        const prev = prevTripMap[trip.id];
        if (prev !== undefined && cur && prev !== cur && hasStaleRate(trip)) changedTripIds.add(trip.id);
      });
    };
    collectTrips(c6Trips?.yearly);
    Object.values(c6Trips?.monthly || {}).forEach(collectTrips);
    prevSpendTripCurrencyRef.current = nextTripMap;

    if (changedMonths.length === 0 && !yearlyChanged && changedTripIds.size === 0) return;
    if (changedMonths.length > 0) {
      setMonthlyData((previousMonths) => {
        let changed = false;
        const nextMonths = { ...previousMonths };
        changedMonths.forEach((monthKey) => {
          if (previousMonths?.[monthKey] && hasStaleRate(previousMonths[monthKey])) {
            nextMonths[monthKey] = stripRow(previousMonths[monthKey]);
            changed = true;
          }
        });
        return changed ? nextMonths : previousMonths;
      });
    }
    if (yearlyChanged) {
      setYearlyData((previous) => (hasStaleRate(previous) ? stripRow(previous) : previous));
    }
    if (changedTripIds.size > 0) {
      setC6Trips((previous) => ({
        yearly: (previous.yearly || []).map((trip) => (changedTripIds.has(trip.id) && hasStaleRate(trip) ? stripRow(trip) : trip)),
        monthly: Object.fromEntries(
          Object.entries(previous.monthly || {}).map(([monthKey, trips]) => [
            monthKey,
            (trips || []).map((trip) => (changedTripIds.has(trip.id) && hasStaleRate(trip) ? stripRow(trip) : trip)),
          ]),
        ),
      }));
    }
  }, [monthlyData, yearlyData, c6Trips, scope3Method, spendCurrencyFallback, reportingYearType, reportingYear, getActualYearForMonth, setMonthlyData, setYearlyData, setC6Trips]);
  
  // Update matched formula ID when it changes
  useEffect(() => {
    if (currentFormulaId !== matchedFormulaId) {
      setMatchedFormulaId(currentFormulaId);
    }
  }, [currentFormulaId, matchedFormulaId]);

  // ============================================================================
  // OCR PREFILL PHASE 2
  // Apply quantity to the correct field after formConfig/dynamicInputFields are available
  // ============================================================================
  useEffect(() => {
    console.log('[OCR Debug] Phase 2 effect triggered:', { 
      ocrPendingQuantity, 
      dynamicInputFieldsLength: dynamicInputFields?.length,
      dynamicInputFields: dynamicInputFields?.map(f => f.fieldKey)
    });
    
    if (!ocrPendingQuantity) {
      console.log('[OCR Debug] Phase 2 - No pending quantity, skipping');
      return;
    }
    const hasNumericValue = (value) => value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value));
    if (!dynamicInputFields || dynamicInputFields.length === 0) {
      console.log('[OCR Debug] Phase 2 - No dynamicInputFields yet, waiting...');
      if (loadingFormConfig) return;
      if (hasNumericValue(ocrPendingQuantity.quantity) && ocrPendingQuantity.method !== 'spend_basis') {
        setMonthlyData((previous) => ({
          ...previous,
          [ocrPendingQuantity.monthKey]: {
            ...(previous[ocrPendingQuantity.monthKey] || {}),
            quantity: Number(ocrPendingQuantity.quantity),
            unit: ocrPendingQuantity.unit || '',
          },
        }));
        setOcrPendingQuantity(null);
      }
      return;
    }
    
    console.log('[OCR Debug] Phase 2 - Finding primary field...');
    const isSpendImport = ocrPendingQuantity.method === 'spend_basis';
    const primaryField = !isSpendImport && hasNumericValue(ocrPendingQuantity.quantity)
      ? findPrimaryActivityField(dynamicInputFields)
      : null;

    if (primaryField) {
      console.log('[OCR Prefill] Phase 2 - Found primary field:', primaryField.fieldKey);
      
      // Get available units for the primary field (for unit normalization)
      let availableUnits = [];
      const isScope3Like = scope === 'scope3' || (scope === 'biogenic' && biogenicScopeSelection === 'scope3');
      if (primaryField.unitSource === 'fuel') {
        if (isScope3Like && requiresSubcategory && !selectedFuel && scope3ActivityId) {
          const matchedActivity = filteredScope3Activities.find(a => a.id === scope3ActivityId);
          availableUnits = matchedActivity?.allowed_units || [];
        } else {
          availableUnits = selectedFuel?.allowed_units || [];
        }
      } else if (primaryField.unitSource === 'all_units') {
        availableUnits = centralizedUnits.map(u => u.symbol);
      } else if (primaryField.unitSource === 'scope3_ef') {
        const matchedEF = scope3ActivityId ? filteredScope3Activities.find(a => a.id === scope3ActivityId) : null;
        if (matchedEF?.allowed_units?.length > 0) {
          availableUnits = matchedEF.allowed_units;
        } else if (primaryField.allowedUnits?.length > 0) {
          availableUnits = primaryField.allowedUnits;
        } else if (primaryField.expectedUnit) {
          availableUnits = [primaryField.expectedUnit];
        }
      } else {
        availableUnits = primaryField.allowedUnits?.length > 0 ? primaryField.allowedUnits : [primaryField.expectedUnit].filter(Boolean);
      }
      
      applyOcrQuantityToField(
        ocrPendingQuantity.monthKey,
        ocrPendingQuantity.quantity,
        ocrPendingQuantity.unit,
        primaryField,
        setMonthlyData,
        availableUnits
      );
    } else {
      console.log('[OCR Debug] Phase 2 - No primary quantity field found or quantity is not applicable.');
    }

    setMonthlyData((previous) => {
      const currentMonth = { ...(previous[ocrPendingQuantity.monthKey] || {}) };
      const assignMappedValue = (patterns, value, unit) => {
        if (!hasNumericValue(value)) return;
        const field = dynamicInputFields.find((candidate) => {
          const key = String(candidate.variable || candidate.fieldKey || '').toLowerCase();
          return patterns.some((pattern) => key === pattern || key.includes(pattern));
        });
        if (!field) return;
        const key = field.variable || field.fieldKey;
        currentMonth[key] = Number(value);
        if (unit) currentMonth[`${key}_unit`] = unit;
      };
      if (isSpendImport) assignMappedValue(['spent_value'], ocrPendingQuantity.cost, ocrPendingQuantity.currency);
      assignMappedValue(['distance', 'km_travelled'], ocrPendingQuantity.distance, 'km');
      return { ...previous, [ocrPendingQuantity.monthKey]: currentMonth };
    });
    setOcrPendingQuantity(null);
  }, [ocrPendingQuantity, dynamicInputFields, loadingFormConfig, findPrimaryActivityField, applyOcrQuantityToField, scope, biogenicScopeSelection, requiresSubcategory, selectedFuel, scope3ActivityId, filteredScope3Activities, centralizedUnits]);

  // Initialize unit values in monthlyData when dynamicInputFields or selectedFuel changes
  // This ensures that units are always explicitly set, not relying on dropdown display fallbacks
  // EXCEPTION: For supplier_basis method, units should remain blank so users explicitly enter them
  useEffect(() => {
    if (dynamicInputFields.length === 0 || activeMonths.length === 0) return;
    
    // For supplier_basis method, do NOT auto-initialize units - they must be entered by user
    if (scope3Method === 'supplier_basis') return;
    
    setMonthlyData(prev => {
      const updated = { ...prev };
      
      activeMonths.forEach(month => {
        const monthKey = month.key;
        const monthData = updated[monthKey] || {};
        let needsUpdate = false;
        
        dynamicInputFields.forEach(field => {
          const unitKey = `${field.variable}_unit`;
          // Only initialize if not already set
          if (!monthData[unitKey]) {
            let fieldUnits = [];
            const isScope3Like = scope === 'scope3' || (scope === 'biogenic' && biogenicScopeSelection === 'scope3');
            if (field.unitSource === 'fuel') {
              // For Scope 3 subcategory categories (C8, C10, C11, C13, C14), fallback to filteredScope3Activities
              if (isScope3Like && requiresSubcategory && !selectedFuel && scope3ActivityId) {
                const matchedActivity = filteredScope3Activities.find(a => a.id === scope3ActivityId);
                fieldUnits = matchedActivity?.allowed_units || [];
              } else {
                fieldUnits = selectedFuel?.allowed_units || [];
              }
            } else if (field.unitSource === 'all_units') {
              // For all_units, use all centralized units (simple + compound)
              fieldUnits = centralizedUnits.map(u => u.symbol);
            } else if (field.unitSource === 'scope3_ef') {
              // For scope3_ef: Priority 1: scope3_ef.allowed_units, Priority 2: field mapping allowed_units, Priority 3: formula expected_unit
              const matchedEF = scope3ActivityId ? filteredScope3Activities.find(a => a.id === scope3ActivityId) : null;
              if (matchedEF?.allowed_units?.length > 0) {
                fieldUnits = matchedEF.allowed_units;
              } else if (field.allowedUnits?.length > 0) {
                fieldUnits = field.allowedUnits;
              } else if (field.expectedUnit) {
                fieldUnits = [field.expectedUnit];
              } else {
                fieldUnits = [];
              }
            } else {
              fieldUnits = field.allowedUnits?.length > 0 ? field.allowedUnits : [field.expectedUnit].filter(Boolean);
            }
            
            const configuredDefaultUnit = field.defaultUnit
              || field.default_unit
              || field.expectedUnit;
            // Fuel quantity mappings may retain a generic schema default (for
            // example, kg) that is not valid for the selected fuel. The unit
            // persisted in row state must match the fuel-backed dropdown.
            const initialUnit = field.unitSource === 'fuel'
              ? (fieldUnits.includes(configuredDefaultUnit) ? configuredDefaultUnit : fieldUnits[0])
              : (configuredDefaultUnit || fieldUnits[0]);
            if (initialUnit) {
              monthData[unitKey] = initialUnit;
              needsUpdate = true;
            }
          }
        });
        
        if (needsUpdate) {
          updated[monthKey] = monthData;
        }
      });
      
      return updated;
    });
  }, [dynamicInputFields, selectedFuel, activeMonths, centralizedUnits, scope3ActivityId, filteredScope3Activities, scope, biogenicScopeSelection, requiresSubcategory, scope3Method]);

  // The compact monthly ledger can display a configured default without mounting
  // DynamicFieldRenderer, which is normally responsible for persisting it. Once
  // a user starts a month, materialize required defaults (for example,
  // Oxidation Factor = 1) into state so validation and the calculation payload
  // consume the same value the user sees. Do not populate untouched months.
  useEffect(() => {
    if (dynamicInputFields.length === 0 || activeMonths.length === 0) return;

    setMonthlyData((previousMonths) => {
      let changed = false;
      const nextMonths = { ...previousMonths };

      activeMonths.forEach((month) => {
        const monthKey = month.key;
        const currentMonth = previousMonths[monthKey] || {};
        const hasStartedMonth = dynamicInputFields.some((field) => {
          if (field.isOverride || field.presentationOnly) return false;
          const value = currentMonth[field.variable] ?? currentMonth[field.fieldKey];
          return value !== '' && value !== null && value !== undefined;
        });
        if (!hasStartedMonth) return;

        let nextMonth = currentMonth;
        dynamicInputFields.forEach((field) => {
          const hasDefault = field.defaultValue !== undefined
            && field.defaultValue !== null
            && field.defaultValue !== '';
          const hasStoredValue = Object.prototype.hasOwnProperty.call(currentMonth, field.variable)
            || Object.prototype.hasOwnProperty.call(currentMonth, field.fieldKey);
          if (!field.required || field.isOverride || field.presentationOnly || !hasDefault || hasStoredValue) return;
          if (nextMonth === currentMonth) nextMonth = { ...currentMonth };
          nextMonth[field.variable] = field.defaultValue;
          changed = true;
        });

        if (nextMonth !== currentMonth) nextMonths[monthKey] = nextMonth;
      });

      return changed ? nextMonths : previousMonths;
    });
  }, [activeMonths, dynamicInputFields, monthlyData, setMonthlyData]);

  // Initialize unit values in yearlyData when dynamicInputFields or selectedFuel changes
  // This ensures that units are always explicitly set for yearly mode, similar to monthly
  // EXCEPTION: For supplier_basis method, units should remain blank so users explicitly enter them
  useEffect(() => {
    if (frequencyType !== 'yearly' || dynamicInputFields.length === 0) return;
    
    // For supplier_basis method, do NOT auto-initialize units - they must be entered by user
    if (scope3Method === 'supplier_basis') return;
    
    setYearlyData(prev => {
      const updated = { ...prev };
      let needsUpdate = false;
      
      dynamicInputFields.forEach(field => {
        const unitKey = `${field.variable}_unit`;
        // Only initialize if not already set
        if (!updated[unitKey]) {
          let fieldUnits = [];
          const isScope3Like = scope === 'scope3' || (scope === 'biogenic' && biogenicScopeSelection === 'scope3');
          if (field.unitSource === 'fuel') {
            // For Scope 3 subcategory categories (C8, C10, C11, C13, C14), fallback to filteredScope3Activities
            if (isScope3Like && requiresSubcategory && !selectedFuel && scope3ActivityId) {
              const matchedActivity = filteredScope3Activities.find(a => a.id === scope3ActivityId);
              fieldUnits = matchedActivity?.allowed_units || [];
            } else {
              fieldUnits = selectedFuel?.allowed_units || [];
            }
          } else if (field.unitSource === 'all_units') {
            fieldUnits = centralizedUnits.map(u => u.symbol);
          } else if (field.unitSource === 'scope3_ef') {
            const matchedEF = scope3ActivityId ? filteredScope3Activities.find(a => a.id === scope3ActivityId) : null;
            if (matchedEF?.allowed_units?.length > 0) {
              fieldUnits = matchedEF.allowed_units;
            } else if (field.allowedUnits?.length > 0) {
              fieldUnits = field.allowedUnits;
            } else if (field.expectedUnit) {
              fieldUnits = [field.expectedUnit];
            }
          } else {
            fieldUnits = field.allowedUnits?.length > 0 ? field.allowedUnits : [field.expectedUnit].filter(Boolean);
          }
          
          const configuredDefaultUnit = field.defaultUnit
            || field.default_unit
            || field.expectedUnit;
          // Keep yearly state aligned with the selected fuel-backed unit,
          // rather than retaining an invalid generic schema default.
          const initialUnit = field.unitSource === 'fuel'
            ? (fieldUnits.includes(configuredDefaultUnit) ? configuredDefaultUnit : fieldUnits[0])
            : (configuredDefaultUnit || fieldUnits[0]);
          if (initialUnit) {
            updated[unitKey] = initialUnit;
            needsUpdate = true;
          }
        }
      });
      
      return needsUpdate ? updated : prev;
    });
  }, [frequencyType, dynamicInputFields, selectedFuel, centralizedUnits, scope3ActivityId, filteredScope3Activities, scope, biogenicScopeSelection, requiresSubcategory, scope3Method]);

  // When scope3ActivityId changes, update the units for scope3_ef fields based on the new activity's allowed_units
  useEffect(() => {
    if (!scope3ActivityId || dynamicInputFields.length === 0 || activeMonths.length === 0) return;
    
    const matchedEF = filteredScope3Activities.find(a => a.id === scope3ActivityId);
    
    setMonthlyData(prev => {
      const updated = { ...prev };
      
      activeMonths.forEach(month => {
        const monthKey = month.key;
        const monthData = { ...(updated[monthKey] || {}) };
        let needsUpdate = false;
        
        dynamicInputFields.forEach(field => {
          if (field.unitSource === 'scope3_ef') {
            const unitKey = `${field.variable}_unit`;
            const currentUnit = monthData[unitKey];
            
            // Determine available units: Priority 1: scope3_ef, Priority 2: field mapping, Priority 3: formula expected_unit
            let availableUnits = [];
            if (matchedEF?.allowed_units?.length > 0) {
              availableUnits = matchedEF.allowed_units;
            } else if (field.allowedUnits?.length > 0) {
              availableUnits = field.allowedUnits;
            } else if (field.expectedUnit) {
              availableUnits = [field.expectedUnit];
            }
            
            // Update if unit not set OR if current unit is not in the available units
            if (availableUnits.length > 0 && (!currentUnit || !availableUnits.includes(currentUnit))) {
              monthData[unitKey] = availableUnits[0];
              needsUpdate = true;
            }
          }
        });
        
        if (needsUpdate) {
          updated[monthKey] = monthData;
        }
      });
      
      return updated;
    });
  }, [scope3ActivityId, filteredScope3Activities, dynamicInputFields, activeMonths]);

  // When fuelId changes, validate/update units for fuel-based fields
  // This fixes the Scope 2 → Scope 1 bug where unit defaults to "Kg" instead of fuel's allowed units
  useEffect(() => {
    if (!fuelId || !selectedFuel || dynamicInputFields.length === 0) return;
    
    const allowedUnits = selectedFuel?.allowed_units || [];
    if (allowedUnits.length === 0) return; // No units to validate against
    
    // Update monthly data units atomically with the selected fuel. This also
    // keeps the legacy row-level `unit` alias aligned with the canonical field.
    setMonthlyData((previousMonths) => {
      let changed = false;
      const nextMonths = { ...previousMonths };
      activeMonths.forEach((month) => {
        const monthKey = month.key;
        const current = previousMonths[monthKey] || {};
        const normalized = normalizeFuelBackedFieldUnits({
          data: current,
          fields: dynamicInputFields,
          allowedUnits,
          isProcessEmissions: ghgFormContext.isProcessCategory,
        });
        if (normalized !== current) {
          nextMonths[monthKey] = normalized;
          changed = true;
        }
      });
      return changed ? nextMonths : previousMonths;
    });

    setYearlyData((previous) => normalizeFuelBackedFieldUnits({
      data: previous,
      fields: dynamicInputFields,
      allowedUnits,
      isProcessEmissions: ghgFormContext.isProcessCategory,
    }));
  }, [
    activeMonths,
    dynamicInputFields,
    fuelId,
    ghgFormContext.isProcessCategory,
    selectedFuel,
    setMonthlyData,
    setYearlyData,
  ]);


  // Build decision inputs automatically based on which fields have values
  // Uses flexible maps_to_context_value_when_filled/empty from mapping config
  const buildDecisionInputs = useCallback((monthData) => {
    const decisionInputs = {};
    
    dynamicInputFields.forEach(field => {
      if (field.mapsToContext) {
        // If this field maps to a context variable, set it based on whether value is provided
        const value = monthData[field.variable] || monthData[field.fieldKey];
        const hasValue = value !== undefined && value !== null && value !== '';
        // Use configurable values instead of hardcoded 'true'/'false'
        decisionInputs[field.mapsToContext] = hasValue 
          ? field.mapsToContextValueWhenFilled 
          : field.mapsToContextValueWhenEmpty;
      }
    });
    
    // Add all decision field values (includes calculation_method_scope3, activity_type, etc.)
    Object.entries(decisionFieldValues).forEach(([key, value]) => {
      if (value) {
        decisionInputs[key] = value;
      }
    });
    
    // Check if this is biogenic scope3
    const isBiogenicScope3 = scope === 'biogenic' && biogenicScopeSelection === 'scope3';
    
    // Backwards compatibility: also set from scope3Method if decisionFieldValues doesn't have it
    if ((scope === 'scope3' || isBiogenicScope3) && scope3Method && !decisionInputs['calculation_method_scope3']) {
      decisionInputs['calculation_method_scope3'] = scope3Method;
    }
    
    // For biogenic scope3 with subcategory categories (C8/C10/C11/C13/C14),
    // pass 'biogenic' as subcategory_selection to satisfy the decision tree
    // (biogenic skips subcategory UI but backend decision tree still expects it)
    if (isBiogenicScope3) {
      const catLower = category?.toLowerCase() || '';
    const isSubcategoryCategory = hasSubcategoryCapability;
      if (isSubcategoryCategory && !decisionInputs['subcategory_selection']) {
        // Use 'biogenic' as subcategory - will be handled by decision tree
        decisionInputs['subcategory_selection'] = 'biogenic';
      }
    }
    
    // For Scope 1/Biogenic Scope 1 Stationary Combustion: default calculation_methodology
    const isBiogenicScope1 = scope === 'biogenic' && biogenicScopeSelection === 'scope1';
    if ((scope === 'scope1' || isBiogenicScope1) && !decisionInputs['calculation_methodology']) {
      decisionInputs['calculation_methodology'] = 'using_heat_basis_ncv';
    }

    // Route from the selected compound-unit denominator. Calculation adapters
    // may further normalize a reverse density conversion and override this
    // basis immediately before execution.
    if (decisionInputs.calculation_methodology === 'using_qty_basis_ef') {
      const efField = dynamicInputFields.find((field) => (
        field.variable === 'ef_quantity' || field.fieldKey === 'ef_quantity'
      ));
      const efUnit = useCustomFuel
        ? monthData?.custom_ef_unit || 'kgCO2/kg'
        : monthData?.ef_quantity_unit
        || monthData?.ef_quantity?.unit
        || efField?.defaultUnit
        || efField?.default_unit
        || efField?.expectedUnit
        || efField?.allowedUnits?.[0];
      const basis = resolveProcessEfDenominatorBasis(efUnit, centralizedUnits);
      if (basis) decisionInputs.ef_quantity_basis = basis;
    }

    // Heat Basis routes internally from the selected CV denominator. The CV
    // unit remains the single user-facing choice, while the tree selects the
    // matching mass or volume formula.
    if (decisionInputs.calculation_methodology === 'using_heat_basis_ncv') {
      const cvField = dynamicInputFields.find((field) => (
        field.variable === 'cv' || field.fieldKey === 'cv'
      ));
      const cvUnit = (useCustomFuel ? monthData?.custom_cv_unit : null)
        || monthData?.cv_unit
        || monthData?.cv?.unit
        || selectedFuel?.calorific_value_unit
        || cvField?.defaultUnit
        || cvField?.default_unit
        || cvField?.expectedUnit
        || cvField?.allowedUnits?.[0];
      const basis = resolveCompoundDenominatorBasis(cvUnit, centralizedUnits);
      if (basis) decisionInputs.cv_quantity_basis = basis;
    }

    return decisionInputs;
  }, [dynamicInputFields, scope, scope3Method, decisionFieldValues, biogenicScopeSelection, centralizedUnits, useCustomFuel, selectedFuel]);

  // Execute yearly calculation (dry_run) - similar to executeCalcEngine but for yearly data
  const executeYearlyCalcEngine = useCallback(async () => {
    if (!formConfig || frequencyType !== 'yearly') {
      return null;
    }
    
    const isScope3Like = scope === 'scope3' || (scope === 'biogenic' && biogenicScopeSelection === 'scope3');
    const effectiveScope = isScope3Like ? 'scope3' : scope;
    
    if (isScope3Like) {
      if (!scope3Method) return null;
      if (scope3Method === 'supplier_basis' && useCustomActivity) {
        if (!scope3CustomActivity?.trim()) return null;
      } else {
        if (!scope3ActivityId) return null;
      }
    } else {
      if (!useCustomFuel && (!selectedFuel || !fuelId)) return null;
    }
    
    const categoryObj = dynamicCategories.find(c => c.name === category && c.scope_code === effectiveScope);
    if (!categoryObj?.id) return null;
    
    // Check if we have any yearly data to calculate
    const hasYearlyData = Object.entries(yearlyData).some(([key, val]) => 
      !key.endsWith('_unit') && val !== '' && val !== null && val !== undefined
    );
    if (!hasYearlyData) return null;
    
    setIsCalculatingYearly(true);
    try {
      // Build inputs from yearly data
      const inputs = {};
      const matchedActivityForYearly = scope3ActivityId ? filteredScope3Activities.find(a => a.id === scope3ActivityId) : null;
      
      dynamicInputFields.forEach(field => {
        const value = yearlyData[field.variable];
        if (value !== undefined && value !== null && value !== '') {
          // Determine base unit
          let baseUnit = field.expectedUnit;
          
          if (field.unitSource === 'fuel') {
            if (isScope3Like && requiresSubcategory && !selectedFuel && scope3ActivityId) {
              baseUnit = yearlyData[`${field.variable}_unit`] || matchedActivityForYearly?.allowed_units?.[0] || matchedActivityForYearly?.default_unit || field.expectedUnit || 'kg';
            } else {
              baseUnit = yearlyData[`${field.variable}_unit`] || selectedFuel?.allowed_units?.[0] || field.expectedUnit;
            }
          } else if (field.unitSource === 'scope3_ef') {
            // For scope3_ef: use yearlyData unit, or fallback to matched activity's default/allowed units
            baseUnit = yearlyData[`${field.variable}_unit`] || matchedActivityForYearly?.default_unit || matchedActivityForYearly?.allowed_units?.[0] || field.expectedUnit || 'kg';
          } else {
            baseUnit = yearlyData[`${field.variable}_unit`] || field.expectedUnit || '';
          }
          
          // Count-based fields should not have units
          const isUnitlessCountField = ['qty_passenger', 'qty_passengers', 'qty_nights', 'qty_room', 'qty_rooms', 
            'number_of_passengers', 'number_of_nights', 'number_of_rooms', 'qty_days_travelled', 'working_days',
            'units_produced', 'products_expected_usage', 'no_of_employees'].includes(field.variable);
          
          // Apply compound suffix if field has compoundWithVariable
          let finalUnit = isUnitlessCountField ? '' : (baseUnit || 'kg');
          if (!isUnitlessCountField && field.compoundWithVariable) {
            const linkedUnit = yearlyData[`${field.compoundWithVariable}_unit`];
            if (linkedUnit && typeof linkedUnit === 'string' && linkedUnit.trim()) {
              // Only add suffix if baseUnit doesn't already contain it
              if (!finalUnit.includes('/')) {
                finalUnit = `${finalUnit}/${linkedUnit.trim()}`;
              }
            }
          }
          
          inputs[field.variable] = { value: parseFloat(value), unit: finalUnit };
        }
      });
      
      // Build context
      const matchedEFForContext = filteredScope3Activities.find(a => a.id === scope3ActivityId);
      
      // For Scope 3 subcategory categories (C8, C10, C11, C13, C14) with fugitive emissions,
      // use the activity name as fuel_name since the activity IS the fuel (e.g., "HFC-32")
      // Skip this for supplier_basis as it uses a basic formula without fuel_database lookup
      let fuelNameForContext = useCustomFuel ? customFuelName : (selectedFuel?.fuel_name || '');
      if (isScope3Like && requiresSubcategory && scope3Method !== 'supplier_basis' && scope3Subcategory === 'fugitive_emissions' && matchedEFForContext?.activity) {
        fuelNameForContext = matchedEFForContext.activity;
      }
      
      // Build yearly reporting period for currency conversion lookup
      const yearlyReportingPeriodForCalc = reportingYearType === 'financial' 
        ? `FY ${reportingYear}-${(parseInt(reportingYear) + 1).toString().slice(-2)}`
        : `CY${reportingYear}`;
      
      const context = {
        fuel_name: fuelNameForContext,
        fuel_id: useCustomFuel ? null : (fuelId || ''),
        scope: effectiveScope,
        category: category,
        facility_id: facilityId,
        reporting_period: yearlyReportingPeriodForCalc, // For currency conversion year lookup
        reporting_year: Number.parseInt(reportingYear, 10),
        reporting_year_type: reportingYearType,
        is_custom_fuel: useCustomFuel || false,
        ...(isScope3Like && {
          calculation_method_scope3: scope3Method,
          scope3_ef_id: scope3ActivityId,
          scope3_ef_default_unit: matchedEFForContext?.default_unit || '',
          // For supplier_basis with custom activity, use the custom activity name
          activity: (scope3Method === 'supplier_basis' && useCustomActivity) 
            ? scope3CustomActivity 
            : matchedEFForContext?.activity,
        }),
      };
      
      // Build user overrides (for fields marked as is_override)
      const userOverrides = {};
      dynamicInputFields.forEach(field => {
        if (field.isOverride && yearlyData[`override_${field.variable}`]) {
          const value = yearlyData[field.variable] || yearlyData[field.fieldKey];
          if (value !== undefined && value !== null) {
            userOverrides[field.variable] = {
              value: parseFloat(value),
              unit: yearlyData[`${field.variable}_unit`] || field.expectedUnit || 'kg'
            };
          }
        }
      });
      
      // Build decision inputs AUTOMATICALLY based on what's filled
      const baseDecisionInputs = buildDecisionInputs(yearlyData);
      const preparedCalculation = prepareDensityAwareCalculationInputs({
        inputs,
        calculationMethodology: baseDecisionInputs.calculation_methodology,
        fields: dynamicInputFields,
        data: yearlyData,
        selectedFuel: useCustomFuel ? null : selectedFuel,
        centralizedUnits,
      });
      if (preparedCalculation.error) {
        setYearlyCalcResult(null);
        return null;
      }
      const decisionInputs = {
        ...baseDecisionInputs,
        ...preparedCalculation.decisionInputs,
      };
      
      const response = await axios.post(
        `${API}/calc-engine/execute-by-category`,
        {
          category_id: categoryObj.id,
          decision_inputs: decisionInputs,
          inputs: preparedCalculation.inputs,
          context: context,
          user_overrides: userOverrides,
          dry_run: true,
          // Pass scope3_ef_id at top level for backend to lookup fuel_database (fugitive emissions)
          ...(isScope3Like && scope3ActivityId && { scope3_ef_id: scope3ActivityId }),
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.ok) {
        setYearlyCalcResult(response.data);
        return response.data;
      }
      setYearlyCalcResult(null);
      return null;
    } catch (error) {
      console.error('Yearly calc engine error:', error);
      setYearlyCalcResult(null);
      return null;
    } finally {
      setIsCalculatingYearly(false);
    }
  }, [formConfig, frequencyType, selectedFuel, fuelId, dynamicCategories, category, scope, facilityId, dynamicInputFields, yearlyData, buildDecisionInputs, getAuthHeader, scope3Method, scope3ActivityId, filteredScope3Activities, useCustomActivity, scope3CustomActivity, requiresSubcategory, scope3Subcategory, biogenicScopeSelection, reportingYearType, reportingYear]);

  // Get fuels for selected category and scope with region + year priority
  const fuelsForCategory = useMemo(() => {
    let filtered = fuelDatabase.filter(f => {
      // Check scope
      if (f.scope !== scope) return false;
      // Check if fuel's categories include the selected category
      const fuelCategories = f.categories?.length > 0 ? f.categories : (f.category ? [f.category] : []);
      return fuelCategories.includes(category);
    });
    
    // Supplier facilities may not yet have a sector. Use Manufacturing as the
    // calculation fallback until the supplier selects one in Facility details.
    const facilitySector = selectedFacility?.sector?.trim() || (supplierContext ? 'Manufacturing' : '');
    if (facilitySector) {
      filtered = filtered.filter(fuel => {
        if (fuel.industry_sectors && fuel.industry_sectors.length > 0) {
          return fuel.industry_sectors.some(s => 
            s.toLowerCase() === facilitySector.toLowerCase()
          );
        }
        return true;
      });
    }
    
    // Get facility country for region filtering
    const facilityCountry = selectedFacility?.country || '';
    
    // Get target year from reporting year
    const targetYear = parseInt(reportingYear, 10) || new Date().getFullYear();
    
    // Group fuels by name+category to handle region/year variants
    const fuelsByKey = {};
    filtered.forEach(fuel => {
      const key = `${fuel.fuel_name}_${fuel.category}`;
      if (!fuelsByKey[key]) {
        fuelsByKey[key] = [];
      }
      fuelsByKey[key].push(fuel);
    });
    
    /**
     * Select best fuel match based on region + year priority:
     * 1. Region-specific + exact year
     * 2. Region-specific + most recent year before target
     * 3. Region-specific + null year
     * 4. Global + exact year
     * 5. Global + most recent year before target
     * 6. Global + null year
     * 7. Any fallback
     */
    const selectBestMatch = (fuels) => {
      if (!fuels || fuels.length === 0) return null;
      
      const regionSpecific = facilityCountry 
        ? fuels.filter(f => f.region && f.region.toLowerCase() === facilityCountry.toLowerCase())
        : [];
      const globalFuels = fuels.filter(f => f.region?.toLowerCase() === 'global' || !f.region);
      const otherFuels = fuels.filter(f => 
        f.region && f.region.toLowerCase() !== 'global' && 
        (!facilityCountry || f.region.toLowerCase() !== facilityCountry.toLowerCase())
      );
      
      const findBestYearMatch = (fuelGroup) => {
        if (fuelGroup.length === 0) return null;
        
        // Exact year match
        const exactYear = fuelGroup.find(f => f.year_applicable === targetYear);
        if (exactYear) return exactYear;
        
        // Most recent year before target
        const earlierYears = fuelGroup
          .filter(f => f.year_applicable && f.year_applicable < targetYear)
          .sort((a, b) => b.year_applicable - a.year_applicable);
        if (earlierYears.length > 0) return earlierYears[0];
        
        // Null year (timeless)
        const nullYear = fuelGroup.find(f => !f.year_applicable);
        if (nullYear) return nullYear;
        
        // Any year as last resort
        return fuelGroup[0];
      };
      
      let bestMatch = findBestYearMatch(regionSpecific);
      if (bestMatch) return bestMatch;
      
      bestMatch = findBestYearMatch(globalFuels);
      if (bestMatch) return bestMatch;
      
      bestMatch = findBestYearMatch(otherFuels);
      if (bestMatch) return bestMatch;
      
      return fuels[0];
    };
    
    // Select best match for each fuel name+category
    const prioritizedFuels = [];
    Object.values(fuelsByKey).forEach(fuels => {
      const bestMatch = selectBestMatch(fuels);
      if (bestMatch) {
        prioritizedFuels.push(bestMatch);
      }
    });
    
    // IMPORTANT: When editing, ensure the saved fuel is always included in the list
    // The fuel prioritization logic may select a different variant (region/year), 
    // but we need to show the originally saved fuel so it appears selected
    if (fuelId && !prioritizedFuels.some(f => f.id === fuelId)) {
      const savedFuel = fuelDatabase.find(f => f.id === fuelId);
      if (savedFuel) {
        prioritizedFuels.unshift(savedFuel);
      }
    }
    
    return prioritizedFuels;
  }, [fuelDatabase, scope, category, selectedFacility, reportingYear, fuelId]);

  // Filtered fuels based on search term
  const filteredFuelsForCategory = useMemo(() => {
    if (!fuelSearchTerm.trim()) return fuelsForCategory;
    const searchLower = fuelSearchTerm.toLowerCase().trim();
    return fuelsForCategory.filter(fuel => 
      fuel.fuel_name?.toLowerCase().includes(searchLower)
    );
  }, [fuelsForCategory, fuelSearchTerm]);

  // Get allowed units for selected fuel OR scope3 activity
  const allowedUnits = useMemo(() => {
    // Priority 1: Scope 1/2 - use fuel's allowed_units
    if (selectedFuel?.allowed_units?.length > 0) {
      return selectedFuel.allowed_units;
    }
    
    // Priority 2: Scope 3 - use selected activity's allowed_units from scope3_ef
    if (scope === 'scope3' && scope3ActivityId) {
      const matchedEF = filteredScope3Activities.find(a => a.id === scope3ActivityId);
      if (matchedEF?.allowed_units?.length > 0) {
        return matchedEF.allowed_units;
      }
    }
    
    // Return empty array if nothing available
    return [];
  }, [selectedFuel, scope, scope3ActivityId, filteredScope3Activities]);

  const defaultUnit = allowedUnits[0] || '';

  // Get conversion factor from formula parameters (SuperAdmin configured)
  const getConversionFactor = useCallback((paramKey, selectedUnit) => {
    // Find the parameter definition from SuperAdmin
    let param = formulaParameters.find(p => p.parameter_key === paramKey);
    
    // Try common variations if no exact match
    if (!param) {
      param = formulaParameters.find(p => 
        p.parameter_key === paramKey.replace('_fuel', '') ||
        p.parameter_key === paramKey.replace('quantity', 'quantity_fuel')
      );
    }
    
    if (!param || !param.unit_conversions || param.unit_conversions.length === 0) {
      return 1; // No conversion defined
    }
    
    // Find the conversion rule for the selected unit
    const conversion = param.unit_conversions.find(c => 
      c.from_unit?.toLowerCase() === selectedUnit?.toLowerCase()
    );
    
    if (conversion && conversion.multiplier !== 0) {
      // multiplier represents "how many from_unit = 1 to_unit"
      // To convert from from_unit to to_unit, we DIVIDE by multiplier
      return 1 / conversion.multiplier;
    }
    
    return 1;
  }, [formulaParameters]);

  // Find formula for scope using SuperAdmin-configured emission configurations
  const findFormulaForScope = useCallback((targetScope, targetCategory = null, gasType = null) => {
    // Use emission configurations (SuperAdmin-defined mappings)
    let matchingConfigs = emissionConfigurations.filter(c => 
      c.is_active && c.scope === targetScope
    );
    
    // If category specified, prefer configs that match the category
    if (targetCategory) {
      const categoryMatches = matchingConfigs.filter(c => {
        const configCategories = c.categories || (c.category ? [c.category] : []);
        if (configCategories.length === 0) return true; // Config applies to all categories
        return configCategories.some(cat => cat.toLowerCase() === targetCategory.toLowerCase());
      });
      
      if (categoryMatches.length > 0) {
        matchingConfigs = categoryMatches;
      }
    }
    
    // Sort by priority (highest first)
    matchingConfigs.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    // Find formula matching the gas type
    for (const config of matchingConfigs) {
      const formula = formulaDefinitions.find(f => f.id === config.formula_id);
      if (!formula) continue;
      
      if (!gasType) return formula;
      
      const keyLower = (formula.formula_key || '').toLowerCase();
      // Check for specific gas type formulas
      if (gasType === 'co2' && keyLower.includes('co2') && !keyLower.includes('co2e')) return formula;
      if (gasType === 'ch4' && keyLower.includes('ch4')) return formula;
      if (gasType === 'n2o' && keyLower.includes('n2o')) return formula;
      if (gasType === 'co2e' && (keyLower.includes('co2e') || keyLower.includes('total'))) return formula;
      if (gasType === 'electricity' && keyLower.includes('electricity')) return formula;
    }
    
    return null;
  }, [emissionConfigurations, formulaDefinitions]);

  // Get parameter value dynamically from input_mappings (SuperAdmin configured)
  const getParameterValue = useCallback((paramKey, fuel, customParams = {}, inputMappings = []) => {
    // PRIORITY 1: Check for override values in customParams FIRST
    // These are explicitly set when user enables override checkboxes
    // This mirrors the logic in Emissions.js getParameterValueDynamic
    
    // Check for CO2 emission factor override (Custom CO2 Emission Factor Heat Basis)
    // Must check for ALL possible parameter keys used in formulas
    const isEmissionFactorCO2 = paramKey.includes('emission_factor_co2') || 
                                 paramKey.includes('emission_factor_heat') ||
                                 paramKey.includes('ef_co2') ||
                                 paramKey.includes('ef_heat') ||
                                 paramKey === 'co2_emission_factor' ||
                                 paramKey === 'co2_emission_factor_heat' ||
                                 paramKey === 'ef' ||
                                 paramKey.toLowerCase().includes('co2') && paramKey.toLowerCase().includes('emission') && paramKey.toLowerCase().includes('heat');
    
    if (isEmissionFactorCO2) {
      if (customParams.emission_factor_co2 !== undefined && customParams.emission_factor_co2 !== null) {
        return customParams.emission_factor_co2;
      }
    }
    
    // Check for calorific value override
    if (paramKey.includes('calorific') || paramKey === 'ncv' || paramKey === 'cv') {
      if (customParams.calorific_value !== undefined && customParams.calorific_value !== null) {
        return customParams.calorific_value;
      }
      if (customParams.ncv !== undefined && customParams.ncv !== null) {
        return customParams.ncv;
      }
      if (customParams.cv !== undefined && customParams.cv !== null) {
        return customParams.cv;
      }
    }
    
    // Check for density override
    if (paramKey.includes('density')) {
      if (customParams.density !== undefined && customParams.density !== null) {
        return customParams.density;
      }
    }
    
    // Find the input mapping for this parameter
    const mapping = inputMappings.find(m => m.parameter_key === paramKey);
    
    if (mapping) {
      const sourceType = mapping.source_type;
      const sourceField = mapping.source_field;
      
      switch (sourceType) {
        case 'user_input':
          // Get from user-entered values (customParams)
          if (customParams[sourceField] !== undefined && customParams[sourceField] !== null) {
            return customParams[sourceField];
          }
          if (customParams[paramKey] !== undefined && customParams[paramKey] !== null) {
            return customParams[paramKey];
          }
          break;
          
        case 'fuel_database':
          // Get from selected fuel, BUT check customParams first for overrides
          if (customParams[sourceField] !== undefined && customParams[sourceField] !== null) {
            return customParams[sourceField];
          }
          if (customParams[paramKey] !== undefined && customParams[paramKey] !== null) {
            return customParams[paramKey];
          }
          // Fallback to fuel database value
          if (fuel && fuel[sourceField] !== undefined && fuel[sourceField] !== null) {
            return fuel[sourceField];
          }
          break;
          
        case 'formula_parameter':
          // Get from formula parameters (SuperAdmin configured)
          const formulaParam = formulaParameters.find(p => p.parameter_key === sourceField || p.parameter_key === paramKey);
          if (formulaParam?.default_value !== undefined && formulaParam.default_value !== null) {
            return formulaParam.default_value;
          }
          break;
          
        case 'constant':
          // Use the default_value from the mapping as a constant
          if (mapping.default_value !== undefined && mapping.default_value !== null && mapping.default_value !== '') {
            return parseFloat(mapping.default_value) || mapping.default_value;
          }
          break;
      }
      
      // Fallback to default_value if source not found
      if (mapping.default_value !== undefined && mapping.default_value !== null && mapping.default_value !== '') {
        return parseFloat(mapping.default_value) || mapping.default_value;
      }
    }
    
    // Legacy fallback: Check formula parameters directly (for parameters not in input_mappings)
    const formulaParam = formulaParameters.find(p => p.parameter_key === paramKey);
    if (formulaParam?.default_value !== undefined && formulaParam.default_value !== null) {
      return formulaParam.default_value;
    }
    
    // Legacy fallback: Check customParams directly
    if (customParams[paramKey] !== undefined && customParams[paramKey] !== null) {
      return customParams[paramKey];
    }
    
    // Legacy fallback: Check fuel data directly
    if (fuel && fuel[paramKey] !== undefined && fuel[paramKey] !== null) {
      return fuel[paramKey];
    }
    
    return 0;
  }, [formulaParameters]);

  // Execute formula using SuperAdmin-configured components and input_mappings
  const executeFormula = useCallback((formula, fuel, customParams = {}) => {
    if (!formula || !formula.components || formula.components.length === 0) {
      return null;
    }
    
    const selectedUnit = customParams.unit || '';
    const selectedUnitIsVolume = isVolumeUnit(selectedUnit, centralizedUnits);
    
    // Get input_mappings from the formula (SuperAdmin configured)
    const inputMappings = formula.input_mappings || {};
    
    let result = null;
    const steps = [];
    
    for (const comp of formula.components) {
      const condition = comp.condition || 'always';
      
      // Check condition
      let shouldApply = true;
      if (condition === 'volume_units' && !selectedUnitIsVolume) shouldApply = false;
      if (condition === 'mass_units' && selectedUnitIsVolume) shouldApply = false;
      
      if (!shouldApply) continue;
      
      // Use input_mappings to get the value dynamically
      const value = getParameterValue(comp.parameter_key, fuel, customParams, inputMappings);
      
      if (result === null || comp.operation === 'base') {
        result = value;
        // For quantity parameters, add "(Unit Conversion Applied)" to the label
        const isQuantityParam = comp.parameter_key?.includes('quantity') || comp.parameter_name?.toLowerCase().includes('quantity');
        const conversionNote = isQuantityParam ? ' (Unit Conversion Applied)' : '';
        // Format value to 6 decimal places max
        const displayValue = Number.isInteger(value) ? value : parseFloat(value.toFixed(6));
        steps.push(`${comp.parameter_name}${conversionNote} = ${displayValue}`);
      } else {
        // Format value and result to 6 decimal places max
        const displayValue = Number.isInteger(value) ? value : parseFloat(value.toFixed(6));
        switch (comp.operation) {
          case 'multiply':
            result = result * value;
            const displayResultMul = Number.isInteger(result) ? result : parseFloat(result.toFixed(6));
            steps.push(`× ${comp.parameter_name} (${displayValue}) = ${displayResultMul}`);
            break;
          case 'divide':
            result = value !== 0 ? result / value : result;
            const displayResultDiv = Number.isInteger(result) ? result : parseFloat(result.toFixed(6));
            steps.push(`÷ ${comp.parameter_name} (${displayValue}) = ${displayResultDiv}`);
            break;
          case 'add':
            result = result + value;
            const displayResultAdd = Number.isInteger(result) ? result : parseFloat(result.toFixed(6));
            steps.push(`+ ${comp.parameter_name} (${displayValue}) = ${displayResultAdd}`);
            break;
          case 'subtract':
            result = result - value;
            const displayResultSub = Number.isInteger(result) ? result : parseFloat(result.toFixed(6));
            steps.push(`- ${comp.parameter_name} (${displayValue}) = ${displayResultSub}`);
            break;
          default:
            result = result * value;
            const displayResultDef = Number.isInteger(result) ? result : parseFloat(result.toFixed(6));
            steps.push(`× ${comp.parameter_name} (${displayValue}) = ${displayResultDef}`);
        }
      }
    }
    
    return result !== null ? { result, steps, formula_name: formula.formula_name } : null;
  }, [centralizedUnits, getParameterValue]);

  // Handle process names
  const addProcessName = () => {
    setProcessNames([...processNames, { name: '', description: '' }]);
  };

  const removeProcessName = (index) => {
    if (processNames.length > 1) {
      setProcessNames(processNames.filter((_, i) => i !== index));
    }
  };

  const updateProcessName = (index, field, value) => {
    const updated = [...processNames];
    updated[index] = { ...updated[index], [field]: value };
    setProcessNames(updated);
  };

  // Handle monthly data
  const updateMonthData = (monthKey, field, value) => {
    setMonthlyData(prev => ({
      ...prev,
      [monthKey]: {
        ...(prev[monthKey] || {}),
        [field]: value
      }
    }));
  };

  const addC6Trip = useCallback((periodKey) => {
    const trip = {
      id: createC6TripId(),
      qty_days_travelled: 1,
      from_location: fromLocation || '',
      to_location: toLocation || '',
    };
    setC6Trips((previous) => (
      periodKey === 'yearly'
        ? { ...previous, yearly: [...(previous.yearly || []), trip] }
        : {
          ...previous,
          monthly: {
            ...previous.monthly,
            [periodKey]: [...(previous.monthly?.[periodKey] || []), trip],
          },
        }
    ));
  }, [fromLocation, setC6Trips, toLocation]);

  const updateC6Trip = useCallback((periodKey, tripId, field, value) => {
    const updateTrip = (trip) => {
      if (trip.id !== tripId) return trip;
      const nextValue = typeof value === 'function' ? value(trip[field]) : value;
      return { ...trip, [field]: nextValue };
    };
    setC6Trips((previous) => (
      periodKey === 'yearly'
        ? { ...previous, yearly: (previous.yearly || []).map(updateTrip) }
        : {
          ...previous,
          monthly: {
            ...previous.monthly,
            [periodKey]: (previous.monthly?.[periodKey] || []).map(updateTrip),
          },
        }
    ));
  }, [setC6Trips]);

  const removeC6Trip = useCallback((periodKey, tripId) => {
    const withoutTrip = (trips = []) => trips.filter((trip) => trip.id !== tripId);
    setC6Trips((previous) => (
      periodKey === 'yearly'
        ? { ...previous, yearly: withoutTrip(previous.yearly) }
        : {
          ...previous,
          monthly: {
            ...previous.monthly,
            [periodKey]: withoutTrip(previous.monthly?.[periodKey]),
          },
        }
    ));
  }, [setC6Trips]);

  const resetC6TripsForReportingYear = useCallback(() => {
    setC6Trips({ monthly: {}, yearly: [] });
  }, [setC6Trips]);

  const resetC6TripsForFrequency = useCallback((nextFrequency) => {
    setC6Trips((previous) => (
      nextFrequency === 'monthly'
        ? { ...previous, yearly: [] }
        : { ...previous, monthly: {} }
    ));
  }, [setC6Trips]);

  // Handle evidence upload for a monthly or yearly entry
  const handleEvidenceUpload = async (periodKey, file, tripId = null) => {
    if (!file) return;

    const sizeErr = validateFileSize(file);
    if (sizeErr) {
      toast.error(sizeErr);
      return;
    }

    const evidences = tripId
      ? (periodKey === 'yearly'
        ? c6Trips.yearly?.find((entry) => entry.id === tripId)?.evidences || []
        : c6Trips.monthly?.[periodKey]?.find((entry) => entry.id === tripId)?.evidences || [])
      : periodKey === 'yearly'
        ? yearlyData.evidences || []
        : monthlyData[periodKey]?.evidences || [];
    const periodLabel = tripId
      ? 'this business travel trip'
      : periodKey === 'yearly'
        ? 'annual data'
        : `${MONTHS.find((month) => month.key === periodKey)?.name || 'this month'} data`;
    let sha256Hash = null;
    try {
      sha256Hash = await calculateFileSha256(file);
    } catch (error) {
      console.warn('[Evidence] Unable to calculate duplicate-check hash', error);
    }
    if (sha256Hash && !(await confirmDuplicateEvidence(file, sha256Hash, evidences, periodLabel))) {
      return;
    }

    const draftEvidence = {
      filename: file.name,
      file,
      size: file.size,
      content_type: file.type,
      is_draft: true,
      sha256_hash: sha256Hash,
    };
    if (tripId) {
      updateC6Trip(periodKey, tripId, 'evidences', (evidences = []) => [...evidences, draftEvidence]);
    } else if (periodKey === 'yearly') {
      setYearlyData(prev => ({
        ...prev,
        evidences: [...(prev.evidences || []), draftEvidence]
      }));
    } else {
      setMonthlyData(prev => {
        const currentEvidences = prev[periodKey]?.evidences || [];
        return {
          ...prev,
          [periodKey]: {
            ...(prev[periodKey] || {}),
            evidences: [...currentEvidences, draftEvidence]
          }
        };
      });
    }
    toast.success(`Evidence uploaded for ${periodLabel}`);
  };

  const handleC7EmployeeEvidenceUpload = async (employeeId, periodKey, file) => {
    const sizeErr = validateFileSize(file);
    if (sizeErr) throw new Error(sizeErr);
    const employee = employees.find((entry) => entry.id === employeeId);
    const evidences = periodKey === 'yearly'
      ? employee?.yearly_data?.evidences || []
      : employee?.monthly_data?.[periodKey]?.evidences || [];
    const periodLabel = periodKey === 'yearly'
      ? 'annual employee commuting data'
      : `${MONTHS.find((month) => month.key === periodKey)?.name || 'this month'} employee commuting data`;
    let sha256Hash = null;
    try {
      sha256Hash = await calculateFileSha256(file);
    } catch (error) {
      console.warn('[Evidence] Unable to calculate duplicate-check hash', error);
    }
    if (sha256Hash && !(await confirmDuplicateEvidence(file, sha256Hash, evidences, periodLabel))) {
      return;
    }

    const uploadedEvidence = {
      filename: file.name,
      file,
      size: file.size,
      content_type: file.type,
      is_draft: true,
      sha256_hash: sha256Hash,
    };
    setEmployees((previousEmployees) => previousEmployees.map((employee) => {
      if (employee.id !== employeeId) return employee;
      if (periodKey === 'yearly') {
        return {
          ...employee,
          yearly_data: {
            ...(employee.yearly_data || { inputs: {}, emissions: null }),
            evidences: [...(employee.yearly_data?.evidences || []), uploadedEvidence],
          },
        };
      }
      const monthData = employee.monthly_data?.[periodKey] || { inputs: {}, emissions: null };
      return {
        ...employee,
        monthly_data: {
          ...employee.monthly_data,
          [periodKey]: {
            ...monthData,
            evidences: [...(monthData.evidences || []), uploadedEvidence],
          },
        },
      };
    }));
    onFormChange?.();
    toast.success(`Evidence uploaded for ${periodLabel}`);
  };

  const handleC7EmployeeEvidenceRemove = async (employeeId, periodKey, evidenceIndex) => {
    setEmployees((previousEmployees) => previousEmployees.map((entry) => {
      if (entry.id !== employeeId) return entry;
      if (periodKey === 'yearly') {
        return {
          ...entry,
          yearly_data: {
            ...entry.yearly_data,
            evidences: (entry.yearly_data?.evidences || []).filter((_, index) => index !== evidenceIndex),
          },
        };
      }
      return {
        ...entry,
        monthly_data: {
          ...entry.monthly_data,
          [periodKey]: {
            ...entry.monthly_data?.[periodKey],
            evidences: (entry.monthly_data?.[periodKey]?.evidences || []).filter((_, index) => index !== evidenceIndex),
          },
        },
      };
    }));
    onFormChange?.();
    toast.success('Evidence removed from draft');
  };

  const removeEvidence = async (periodKey, evidenceIndexOrTripId, maybeEvidenceIndex = null) => {
    const tripId = maybeEvidenceIndex === null ? null : evidenceIndexOrTripId;
    const evidenceIndex = maybeEvidenceIndex === null ? evidenceIndexOrTripId : maybeEvidenceIndex;
    const trip = tripId
      ? (periodKey === 'yearly'
        ? c6Trips.yearly?.find((entry) => entry.id === tripId)
        : c6Trips.monthly?.[periodKey]?.find((entry) => entry.id === tripId))
      : null;
    const evidences = tripId
      ? trip?.evidences || []
      : periodKey === 'yearly'
      ? yearlyData.evidences || []
      : monthlyData[periodKey]?.evidences || [];
    if (tripId) {
      updateC6Trip(periodKey, tripId, 'evidences', (items = []) => items.filter((_, index) => index !== evidenceIndex));
      return;
    }
    if (periodKey === 'yearly') {
      setYearlyData(prev => ({
        ...prev,
        evidences: (prev.evidences || []).filter((_, index) => index !== evidenceIndex)
      }));
      return;
    }
    updateMonthData(periodKey, 'evidences',
      evidences.filter((_, index) => index !== evidenceIndex)
    );
  };

  const commitDraftEvidence = useCallback(async (savedRecords) => {
    const uploads = savedRecords.flatMap(({ id, evidences = [], drafts = [] }) => [
      ...evidences.map((evidence) => ({ id, evidence })),
      ...drafts,
    ].filter(({ evidence }) => evidence?.is_draft && evidence?.file));
    const results = await Promise.allSettled(uploads.map(({ id, evidence, employeeId, periodKey }) => {
      const uploadData = new FormData();
      uploadData.append('file', evidence.file);
      if (evidence.sha256_hash) uploadData.append('sha256_hash', evidence.sha256_hash);
      const query = new URLSearchParams();
      if (employeeId) query.set('employee_id', employeeId);
      if (periodKey) query.set('period_key', periodKey);
      const endpoint = `${API}/emissions/${id}/evidence${query.size ? `?${query.toString()}` : ''}`;
      return axios.post(endpoint, uploadData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
    }));
    const failures = results.filter((result) => result.status === 'rejected').length;
    if (failures) throw new Error(`${failures} evidence file(s) could not be uploaded`);
  }, [getAuthHeader]);


  // Helper function to render a dynamic field (for cleaner grouping in Step 3)
  // F5: Swap inline renderDynamicField/getFieldUnitsForYearly for shared
  // <DynamicFieldRenderer /> component + getFieldUnits util. This also picks up
  // biogenic+scope3 unit-source handling that was missing from the inline path.
  const computeCompoundSuffix = (field, data) => {
    if (!field?.compoundWithVariable) return '';
    const linked = field.compoundWithVariable;
    const linkedUnit = data?.[`${linked}_unit`];
    return (typeof linkedUnit === 'string' && linkedUnit.trim()) ? linkedUnit.trim() : '';
  };

  const renderDynamicField = (field, monthKey, data) => (
    <DynamicFieldRenderer
      key={`${monthKey}-${field.id || field.variable}`}
      field={field}
      monthKey={monthKey}
      data={data}
      updateMonthData={updateMonthData}
      scope={scope}
      scope3Method={scope3Method}
      scope3ActivityId={scope3ActivityId}
      requiresSubcategory={requiresSubcategory}
      selectedFuel={selectedFuel}
      filteredScope3Activities={filteredScope3Activities}
      centralizedUnits={centralizedUnits}
      biogenicScopeSelection={biogenicScopeSelection}
      useCustomFuel={useCustomFuel}
      frequencyType={frequencyType}
      reportingYear={reportingYear}
      reportingYearType={reportingYearType}
      compoundSuffix={computeCompoundSuffix(field, data)}
    />
  );

  // Helper function to compute field units (same logic as monthly, used for yearly mode)
  const getFieldUnitsForYearly = (field) => {
    const base = getFieldUnitsShared({
      field,
      scope,
      scope3Method,
      scope3ActivityId,
      requiresSubcategory,
      selectedFuel,
      filteredScope3Activities,
      centralizedUnits,
      biogenicScopeSelection,
      useCustomFuel,
    });
    const suffix = computeCompoundSuffix(field, yearlyData);
    if (!suffix) return base;
    return base.map(u => `${u}/${suffix}`);
  };


  // Check if month has data
  const getMonthStatus = (monthKey) => {
    const data = monthlyData[monthKey];
    if (!data) return 'empty';

    if (dynamicInputFields.length > 0) {
      return isMonthlyEntryComplete(data, dynamicInputFields) ? 'filled' : 'empty';
    }
    
    // For regular emissions without dynamic fields, check quantity
    if (!data.quantity || parseFloat(data.quantity) <= 0) return 'empty';
    return 'filled';
  };

  // Count filled months
  const filledMonthsCount = useMemo(() => {
    if (isC6MultiTrip) {
      if (frequencyType === 'yearly') {
        return (c6Trips.yearly || []).filter((trip) => isMonthlyEntryComplete(trip, dynamicInputFields)).length;
      }
      return Object.values(c6Trips.monthly || {}).filter((trips) => (
        trips.some((trip) => isMonthlyEntryComplete(trip, dynamicInputFields))
      )).length;
    }
    // For yearly mode, return 1 if there's yearly data, 0 otherwise
    if (frequencyType === 'yearly') {
      // For C7 Employee Commuting yearly mode
      if (isC7EmployeeCommuting && employees.length > 0) {
        const hasYearlyData = employees.some(emp => 
          emp.yearly_data?.emissions?.co2e !== null && emp.yearly_data?.emissions?.co2e !== undefined
        );
        return hasYearlyData ? 1 : 0;
      }
      
      // For other categories yearly mode, check yearlyData
      const hasYearlyInput = Object.values(yearlyData || {}).some(v => v !== '' && v !== null && v !== undefined);
      return hasYearlyInput ? 1 : 0;
    }
    
    // For C7 Employee Commuting monthly mode, count employees with calculated emissions
    if (isC7EmployeeCommuting && employees.length > 0) {
      // Count unique months that have at least one employee with calculated emissions
      const monthsWithData = new Set();
      employees.forEach(emp => {
        Object.entries(emp.monthly_data || {}).forEach(([monthKey, data]) => {
          const hasInputs = Object.entries(data?.inputs || {}).some(
            ([key, value]) => !key.endsWith('_unit') && value !== '' && value !== null && value !== undefined && value !== 0,
          );
          if (hasInputs || (data?.emissions?.co2e !== null && data?.emissions?.co2e !== undefined)) {
            monthsWithData.add(monthKey);
          }
        });
      });
      return monthsWithData.size;
    }
    
    // A month is complete only when every mandatory, non-override field has a value.
    if (dynamicInputFields.length > 0) {
      return Object.values(monthlyData).filter((monthData) =>
        isMonthlyEntryComplete(monthData, dynamicInputFields),
      ).length;
    }
    
    // No dynamic fields loaded yet - return 0
    return 0;
  }, [c6Trips, dynamicInputFields, employees, frequencyType, isC6MultiTrip, isC7EmployeeCommuting, monthlyData, yearlyData]);

  const c6TripCount = useMemo(() => (
    frequencyType === 'yearly'
      ? (c6Trips.yearly || []).length
      : Object.values(c6Trips.monthly || {}).reduce((total, trips) => total + trips.length, 0)
  ), [c6Trips, frequencyType]);

  // F4: Validation dispatcher delegates to extracted utils.
  // The util `canProceedToStep` covers cases 2/3/4 (legacy case 5 default-true preserved).
  const canProceedToStep = (step, overrides = {}) => canProceedToStepUtil(step, {
    // Step 1 params
    facilityId, scope, category,
    scope3Method, scope3ActivityId, useCustomActivity, scope3CustomActivity,
    biogenicScopeSelection,
    capabilities: resolvedCapabilities,
    useCustomFuel, fuelId, customFuelName, customEmissionFactor, customSource,
    // Step 2 params
    processNames, responsiblePerson, requiresAssetName, assetName,
    // Step 3 params
    isC7EmployeeCommuting, employees: overrides.employees || employees, dynamicInputFields,
    frequencyType,
    yearlyData: submissionYearlyData,
    monthlyData: submissionMonthlyData,
    filledMonthsCount: submissionFilledMonthsCount,
    multiTripRows: overrides.multiTripRows || [],
    updateMonthData,
    calculationMethodology: decisionFieldValues.calculation_methodology,
    selectedFuel,
    centralizedUnits,
    reportingYear,
    reportingYearType,
  });


  const validateFullForm = (stepOrOverrides = {}, maybeOverrides = {}) => {
    const overrides = typeof stepOrOverrides === 'object' ? stepOrOverrides : maybeOverrides;
    for (const step of [2, 3, 4]) {
      const validation = canProceedToStep(step, overrides);
      if (!validation.valid) return validation;
    }
    return { valid: true };
  };

  // Handler for calculating emissions for a specific employee and month
  const handleCalculateEmployeeMonth = useCallback(async (employeeId, monthKey, employee) => {
    setIsCalculatingEmployee(true);
    try {
      // Check if this is yearly mode
      const isYearly = monthKey === 'yearly';
      
      // Get input data based on mode
      const inputData = isYearly ? employee.yearly_data : employee.monthly_data?.[monthKey];
      
      if (!inputData?.inputs || Object.keys(inputData.inputs).length === 0) {
        toast.error('Please enter input values first');
        setIsCalculatingEmployee(false);
        return;
      }

      // Check if all inputs have values
      const hasValidInputs = Object.values(inputData.inputs).some(v => v !== '' && v !== null && v !== undefined);
      if (!hasValidInputs) {
        toast.error('Please enter at least one input value');
        setIsCalculatingEmployee(false);
        return;
      }

      // Validate required fields
      if (!scope3Method) {
        toast.error('Please select a calculation method first');
        setIsCalculatingEmployee(false);
        return;
      }

      if (!scope3ActivityType) {
        toast.error('Please select an activity type first');
        setIsCalculatingEmployee(false);
        return;
      }

      // Find the matched activity from scope3 EF data - MUST use scope3ActivityId
      const activityType = scope3ActivityType;
      
      // For supplier_basis with custom activity (including "others"), skip activity ID requirement
      // Otherwise require specific activity selection - no fallback to avoid picking wrong EF
      if (!useCustomActivity && !scope3ActivityId) {
        toast.error('Please select a specific activity from the dropdown');
        setIsCalculatingEmployee(false);
        return;
      }
      
      // For custom activity, we don't need a matched activity from the dropdown
      let matchedActivity = null;
      if (!useCustomActivity) {
        matchedActivity = filteredScope3Activities.find(a => a.id === scope3ActivityId);

        if (!matchedActivity) {
          toast.error(`Activity not found. Please select a valid activity from the dropdown.`);
          setIsCalculatingEmployee(false);
          return;
        }
      }
      
      // Use the matched activity's emission factor (or null for custom activity - EF comes from user input)
      const efFromActivity = matchedActivity?.emission_factor || null;
      const efUnitFromActivity = matchedActivity?.ef_unit || null;

      // Build decision_inputs for decision tree traversal
      const decisionInputs = {
        calculation_method_scope3: scope3Method,
        activity_type: activityType,
      };

      // Build inputs for formula execution - format: { variable: { value, unit } }
      const formulaInputs = {};
      Object.entries(inputData.inputs).forEach(([key, value]) => {
        if (!key.endsWith('_unit') && value !== '' && value !== null && value !== undefined) {
          // Find the field config to get the unit
          const fieldConfig = dynamicInputFields.find(f => f.variable === key);
          formulaInputs[key] = {
            value: parseFloat(value),
            unit: inputData.inputs[`${key}_unit`] || fieldConfig?.expectedUnit || fieldConfig?.unit || ''
          };
        }
      });

      // Build reporting period for currency conversion lookup (C7 Employee Commuting)
      let c7ReportingPeriod;
      if (isYearly) {
        c7ReportingPeriod = reportingYearType === 'financial' 
          ? `FY ${reportingYear}-${(parseInt(reportingYear) + 1).toString().slice(-2)}`
          : `CY${reportingYear}`;
      } else {
        const monthNumber = {
          jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
          jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
        }[monthKey] || monthKey;
        const actualYear = reportingYearType === 'financial' && Number(monthNumber) <= 3
          ? Number(reportingYear) + 1
          : Number(reportingYear);
        c7ReportingPeriod = `${actualYear}-${monthNumber}`;
      }

      // Build context for additional data
      const calcContext = {
        calculation_method_scope3: scope3Method,
        activity_type: activityType,
        reporting_period: c7ReportingPeriod, // For currency conversion year lookup
        reporting_year_type: reportingYearType,
        activity: matchedActivity?.activity || scope3CustomActivity || 'Custom Activity', // For emission factor lookup
        fuel_name: matchedActivity?.activity || scope3CustomActivity || 'Custom Activity', // Alias for property source mapping
        scope3_ef_id: matchedActivity?.id || null,
        use_custom_activity: useCustomActivity,
      };

      // Get category ID
      const categoryObj = dynamicCategories.find(c => 
        c.name === category && c.scope_code === 'scope3'
      );

      if (!categoryObj) {
        toast.error('Category not found');
        setIsCalculatingEmployee(false);
        return;
      }

      const payload = {
        category_id: categoryObj.id,
        decision_inputs: decisionInputs,
        inputs: formulaInputs,
        context: calcContext,
        scope3_ef_id: matchedActivity?.id || null,
      };

      // Call calc engine
      const response = await axios.post(
        `${API}/calc-engine/execute-by-category`,
        payload,
        { headers: getAuthHeader() }
      );

      if (response.data?.outputs) {
        const co2e = response.data.outputs.co2e?.value || 0;
        
        // Capture formula_id from calculation response
        if (response.data.resolved_formula?.id) {
          setC7FormulaId(response.data.resolved_formula.id);
          setC7FormulaName(response.data.resolved_formula.name || '');
        }
        
        // Store audit log for calculation ledger display
        const auditLog = response.data.audit_log || [];
        const appliedFactors = response.data.applied_factors || {};
        
        // Update employee with calculated emissions and audit data
        setEmployees(prevEmployees => {
          const updatedEmployees = prevEmployees.map(emp => {
            if (emp.id === employeeId) {
              if (isYearly) {
                // YEARLY MODE: Update yearly_data
                return {
                  ...emp,
                  yearly_data: {
                    ...emp.yearly_data,
                    emissions: {
                      co2: response.data.outputs.co2?.value || 0,
                      ch4: response.data.outputs.ch4?.value || 0,
                      n2o: response.data.outputs.n2o?.value || 0,
                      co2e: co2e,
                    },
                    calculation_details: {
                      audit_log: auditLog,
                      applied_factors: appliedFactors,
                      formula_id: response.data.resolved_formula?.id || null,
                      formula_version_id: response.data.resolved_formula?.version_id || null,
                      decision_tree_version_id: response.data.resolved_decision_tree?.version_id || null,
                      formula_name: response.data.resolved_formula?.name || '',
                      emission_factor: `${efFromActivity} ${efUnitFromActivity}`,
                      outputs: response.data.outputs,
                    },
                  },
                };
              } else {
                // MONTHLY MODE: Update monthly_data
                return {
                  ...emp,
                  monthly_data: {
                    ...emp.monthly_data,
                    [monthKey]: {
                      ...emp.monthly_data[monthKey],
                      emissions: {
                        co2: response.data.outputs.co2?.value || 0,
                        ch4: response.data.outputs.ch4?.value || 0,
                        n2o: response.data.outputs.n2o?.value || 0,
                        co2e: co2e,
                      },
                      // Store calculation details for ledger display AND formula_id for save
                      calculation_details: {
                        audit_log: auditLog,
                        applied_factors: appliedFactors,
                        formula_id: response.data.resolved_formula?.id || null,
                        formula_version_id: response.data.resolved_formula?.version_id || null,
                        decision_tree_version_id: response.data.resolved_decision_tree?.version_id || null,
                        formula_name: response.data.resolved_formula?.name || '',
                        outputs: response.data.outputs,
                      },
                    },
                  },
                };
              }
            }
            return emp;
          });
          
          if (isYearly) {
            // For yearly mode, calculate total from all employees' yearly_data
            let yearlyTotalValue = 0;
            updatedEmployees.forEach(emp => {
              yearlyTotalValue += emp.yearly_data?.emissions?.co2e || 0;
            });
            setEmployeeYearlyTotal({ co2e: yearlyTotalValue });
          } else {
            // Recalculate monthly totals
            const newMonthlyTotals = {};
            const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            monthKeys.forEach(mk => {
              let total = 0;
              updatedEmployees.forEach(emp => {
                total += emp.monthly_data?.[mk]?.emissions?.co2e || 0;
              });
              if (total > 0) {
                newMonthlyTotals[mk] = { co2e: total };
              }
            });
            setEmployeeMonthlyTotals(newMonthlyTotals);
            
            // Calculate yearly total from monthly
            const yearlyTotalValue = Object.values(newMonthlyTotals).reduce((sum, m) => sum + (m.co2e || 0), 0);
            setEmployeeYearlyTotal({ co2e: yearlyTotalValue });
          }
          
          return updatedEmployees;
        });
        
        toast.success(`Calculated: ${co2e.toFixed(4)} tCO2e`);
      } else {
        toast.error('No calculation results returned');
      }
    } catch (error) {
      console.error('[MultiEmployee Calc] Error:', error);
      console.error('[MultiEmployee Calc] Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to calculate emissions');
    } finally {
      setIsCalculatingEmployee(false);
    }
  }, [scope3Method, scope3ActivityType, scope3ActivityId, filteredScope3Activities, dynamicCategories, category, dynamicInputFields, getAuthHeader, useCustomActivity, scope3CustomActivity]);

  const calculateC7EmployeesForSave = useCallback(async () => {
    const monthNumbers = {
      jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
      jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
    };
    const fail = (message) => ({ error: message });
    if (!scope3Method) return fail('Please select a calculation method first');
    if (!scope3ActivityType) return fail('Please select an activity type first');
    if (!useCustomActivity && !scope3ActivityId) {
      return fail('Please select a specific activity from the dropdown');
    }

    const matchedActivity = useCustomActivity
      ? null
      : filteredScope3Activities.find((activity) => activity.id === scope3ActivityId);
    if (!useCustomActivity && !matchedActivity) {
      return fail('Activity not found. Please select a valid activity from the dropdown.');
    }
    const categoryObj = dynamicCategories.find((item) => (
      item.name === category && item.scope_code === 'scope3'
    ));
    if (!categoryObj) return fail('Category not found');

    setIsCalculatingEmployee(true);
    try {
      const calculatedEmployees = employees.map((employee) => ({ ...employee }));
      for (let employeeIndex = 0; employeeIndex < calculatedEmployees.length; employeeIndex += 1) {
        let employee = calculatedEmployees[employeeIndex];
        if (!employee.name?.trim()) return fail('Employee Name is required');
        const periods = frequencyType === 'yearly'
          ? ['yearly']
          : Object.entries(employee.monthly_data || {})
            .filter(([, monthData]) => dynamicInputFields.some((field) => {
              const value = monthData?.inputs?.[field.variable];
              return value !== '' && value !== null && value !== undefined;
            }))
            .map(([monthKey]) => monthKey);

        for (const periodKey of periods) {
          const isYearly = periodKey === 'yearly';
          const inputData = isYearly ? employee.yearly_data : employee.monthly_data?.[periodKey];
          const inputs = inputData?.inputs || {};
          for (const field of dynamicInputFields.filter((item) => item.required && !item.isOverride)) {
            const value = inputs[field.variable];
            if (value === '' || value === null || value === undefined) {
              return fail(`${employee.name}: ${field.label} is required`);
            }
          }

          const formulaInputs = {};
          for (const field of dynamicInputFields) {
            const value = inputs[field.variable];
            if (value === '' || value === null || value === undefined) continue;
            const unit = inputs[`${field.variable}_unit`] || field.expectedUnit || field.unit || '';
            if (scope3Method === 'supplier_basis' && field.unitSource !== 'none' && !String(unit).trim()) {
              return fail(`${employee.name}: Unit is required for ${field.label}`);
            }
            formulaInputs[field.variable] = { value: parseFloat(value), unit };
          }

          const reportingPeriod = isYearly
            ? (reportingYearType === 'financial'
              ? `FY ${reportingYear}-${String(Number(reportingYear) + 1).slice(-2)}`
              : `CY${reportingYear}`)
            : (() => {
              const monthNumber = monthNumbers[periodKey] || periodKey;
              const actualYear = reportingYearType === 'financial' && Number(monthNumber) <= 3
                ? Number(reportingYear) + 1
                : Number(reportingYear);
              return `${actualYear}-${monthNumber}`;
            })();

          let response;
          try {
            response = await axios.post(`${API}/calc-engine/execute-by-category`, {
              category_id: categoryObj.id,
              decision_inputs: {
                calculation_method_scope3: scope3Method,
                activity_type: scope3ActivityType,
              },
              inputs: formulaInputs,
              context: {
                calculation_method_scope3: scope3Method,
                activity_type: scope3ActivityType,
                reporting_period: reportingPeriod,
                reporting_year_type: reportingYearType,
                activity: matchedActivity?.activity || scope3CustomActivity || 'Custom Activity',
                fuel_name: matchedActivity?.activity || scope3CustomActivity || 'Custom Activity',
                scope3_ef_id: matchedActivity?.id || null,
                use_custom_activity: useCustomActivity,
              },
              scope3_ef_id: matchedActivity?.id || null,
            }, { headers: getAuthHeader() });
          } catch (error) {
            const detail = error.response?.data?.detail;
            return fail(`${employee.name}: ${typeof detail === 'string' ? detail : 'Failed to calculate emissions'}`);
          }
          if (!response.data?.outputs) return fail(`${employee.name}: No calculation results returned`);

          const emissions = {
            co2: response.data.outputs.co2?.value || 0,
            ch4: response.data.outputs.ch4?.value || 0,
            n2o: response.data.outputs.n2o?.value || 0,
            co2e: response.data.outputs.co2e?.value || 0,
          };
          const calculationDetails = {
            audit_log: response.data.audit_log || [],
            applied_factors: response.data.applied_factors || {},
            formula_id: response.data.resolved_formula?.id || null,
            formula_version_id: response.data.resolved_formula?.version_id || null,
            decision_tree_version_id: response.data.resolved_decision_tree?.version_id || null,
            formula_name: response.data.resolved_formula?.name || '',
            outputs: response.data.outputs,
          };
          if (isYearly) {
            employee = {
              ...employee,
              yearly_data: { ...employee.yearly_data, emissions, calculation_details: calculationDetails },
            };
          } else {
            employee = {
              ...employee,
              monthly_data: {
                ...employee.monthly_data,
                [periodKey]: {
                  ...employee.monthly_data[periodKey],
                  emissions,
                  calculation_details: calculationDetails,
                },
              },
            };
          }
          calculatedEmployees[employeeIndex] = employee;
          if (response.data.resolved_formula?.id) {
            setC7FormulaId(response.data.resolved_formula.id);
            setC7FormulaName(response.data.resolved_formula.name || '');
          }
        }
      }

      setEmployees(calculatedEmployees);
      if (frequencyType === 'yearly') {
        setEmployeeYearlyTotal({
          co2e: calculatedEmployees.reduce((total, employee) => (
            total + (employee.yearly_data?.emissions?.co2e || 0)
          ), 0),
        });
      } else {
        const totals = {};
        calculatedEmployees.forEach((employee) => {
          Object.entries(employee.monthly_data || {}).forEach(([monthKey, monthData]) => {
            if (monthData?.emissions?.co2e === null || monthData?.emissions?.co2e === undefined) return;
            totals[monthKey] = {
              co2e: (totals[monthKey]?.co2e || 0) + monthData.emissions.co2e,
            };
          });
        });
        setEmployeeMonthlyTotals(totals);
        setEmployeeYearlyTotal({
          co2e: Object.values(totals).reduce((total, month) => total + (month.co2e || 0), 0),
        });
      }
      return { employees: calculatedEmployees };
    } finally {
      setIsCalculatingEmployee(false);
    }
  }, [category, dynamicCategories, dynamicInputFields, employees, filteredScope3Activities, frequencyType, getAuthHeader, reportingYear, reportingYearType, scope3ActivityId, scope3ActivityType, scope3CustomActivity, scope3Method, useCustomActivity]);

  // Submit handler - creates emissions for each month with data
  // F6 (Option B): handleSubmit body lifted to useEmissionSubmit hook.
  // Form just assembles the ctx and calls submit().
  const normalizeCarbonCompositionQuantity = useCallback((data = {}) => {
    if (
      !ghgFormContext.isProcessCategory
      || decisionFieldValues.calculation_methodology !== 'using_carbon_composition'
    ) {
      return data;
    }

    const quantityField = dynamicInputFields.find((field) => (
      field.variable === 'quantity_used_process_emissions'
      || field.fieldKey === 'quantity_used_process_emissions'
    ));
    if (!quantityField) return data;

    const configuredKey = quantityField.variable || quantityField.fieldKey;
    const configuredValue = data[configuredKey] ?? data[quantityField.fieldKey];
    if (configuredValue !== undefined && configuredValue !== null && configuredValue !== '') {
      return data;
    }

    const legacyValue = data.qty ?? data.quantity;
    if (legacyValue === undefined || legacyValue === null || legacyValue === '') {
      return data;
    }

    const legacyUnit = data.qty_unit || data.quantity_unit || data.unit;
    return {
      ...data,
      [configuredKey]: legacyValue,
      ...(legacyUnit && { [`${configuredKey}_unit`]: legacyUnit }),
    };
  }, [decisionFieldValues.calculation_methodology, dynamicInputFields, ghgFormContext.isProcessCategory]);

  const normalizeSubmissionUnits = useCallback((data = {}) => normalizeFuelBackedFieldUnits({
    data,
    fields: dynamicInputFields,
    allowedUnits: selectedFuel?.allowed_units || [],
    isProcessEmissions: ghgFormContext.isProcessCategory,
  }), [dynamicInputFields, ghgFormContext.isProcessCategory, selectedFuel]);
  const submissionMonthlyData = useMemo(() => Object.fromEntries(
    Object.entries(monthlyData).map(([monthKey, data]) => [
      monthKey,
      normalizeSubmissionUnits(normalizeCarbonCompositionQuantity(data)),
    ]),
  ), [monthlyData, normalizeCarbonCompositionQuantity, normalizeSubmissionUnits]);
  const submissionYearlyData = useMemo(
    () => normalizeSubmissionUnits(normalizeCarbonCompositionQuantity(yearlyData)),
    [yearlyData, normalizeCarbonCompositionQuantity, normalizeSubmissionUnits],
  );
  const submissionFilledMonthsCount = useMemo(() => (
    Object.values(submissionMonthlyData).filter((monthData) =>
      isMonthlyEntryComplete(monthData, dynamicInputFields),
    ).length
  ), [dynamicInputFields, submissionMonthlyData]);

  const { submit: handleSubmit } = useEmissionSubmit({
    // State
    facilityId, scope, category, fuelId, useCustomFuel, customFuelName,
    customEmissionFactor, customSource, isSaving, scope3Method, spendCurrencyConversionMethod, allocationMethod, scope3ActivityId,
    scope3ActivityType, scope3Subcategory, typeOfProduct, scope3CustomActivity, useCustomActivity,
    biogenicScopeSelection, employees, frequencyType, reportingYearType, reportingYear,
    monthlyData: submissionMonthlyData, yearlyData: submissionYearlyData, processNames, responsiblePerson,
    responsiblePersonDesignation, responsiblePersonContact, notes, recordSource, supplierName,
    supplierCode, employeeName, employeeId, assetName, fromLocation, toLocation,
    dynamicCategories,
    // Setters
    setIsSaving,
    // Computed
    isC7EmployeeCommuting, requiresSubcategory, selectedFuel, capabilities: resolvedCapabilities,
    categoryCode: ghgFormContext.categoryCode,
    isProcessEmissions: ghgFormContext.isProcessCategory,
    filteredScope3Activities, dynamicInputFields, centralizedUnits, defaultUnit,
    matchedFormula: dynamicInputFieldsResult?.matchedFormula,
    isC6MultiTrip,
    c6Trips,
    // Helpers
    canProceedToStep: validateFullForm, getAuthHeader, onSuccess, getActualYearForMonth,
    commitDraftEvidence,
    buildDecisionInputs,
    // Decision state for custom fuel methodology
    decisionFieldValues,
    // Editing
    editingEmission,
    calculateC7EmployeesForSave,
    // Supplier context (optional)
    supplierContext,
    assignedReportingPeriod,
    // OCR context (for finalize-import after save)
    ocrPrefillData,
  });

  const activeCreateModule = useMemo(() => {
    const categoryName = (category || '').toLowerCase();
    if (scope === 'scope3') {
      const categoryCode = categoryName.match(/^(c\d+)/)?.[1];
      return categoryRegistry.get(categoryCode) || categoryRegistry.getGenericModule?.('scope3');
    }
    if (scope === 'scope1') {
      if (categoryName.includes('stationary')) return categoryRegistry.get('stationary_combustion');
      if (categoryName.includes('mobile')) return categoryRegistry.get('mobile_combustion');
      if (categoryName.includes('fugitive')) return categoryRegistry.get('fugitive_emissions');
      return categoryRegistry.getGenericModule?.('scope1');
    }
    if (scope === 'scope2') return categoryRegistry.getGenericModule?.('scope2');
    if (scope === 'biogenic') {
      return biogenicScopeSelection === 'scope3'
        ? categoryRegistry.getGenericModule?.('scope3')
        : categoryRegistry.getGenericModule?.('scope1');
    }
    return null;
  }, [scope, category, biogenicScopeSelection]);

  const CreateDataRenderer = activeCreateModule?.Step3Renderer || Step3YearMonthlyData;

  return (
    <div className="min-w-0 space-y-6" data-testid="single-page-emission-form">
      <AlertDialog open={Boolean(duplicateEvidencePrompt)} onOpenChange={(open) => { if (!open) resolveDuplicateEvidencePrompt(false); }}>
        <AlertDialogContent data-testid="duplicate-evidence-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="duplicate-evidence-dialog-title">Duplicate evidence detected</AlertDialogTitle>
            <AlertDialogDescription data-testid="duplicate-evidence-dialog-description">“{duplicateEvidencePrompt?.filename}” has the same filename and SHA-256 content hash as evidence already attached to {duplicateEvidencePrompt?.periodLabel}. Do you want to upload it again?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => resolveDuplicateEvidencePrompt(false)} data-testid="duplicate-evidence-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => resolveDuplicateEvidencePrompt(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="duplicate-evidence-confirm-button">Upload anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* OCR Import Notice Banner */}
      {ocrPrefillData && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">
              AI-Assisted Entry from Invoice
            </p>
            <p className="text-sm text-blue-600 mt-1">
              This form has been pre-filled from your uploaded invoice
              {ocrPrefillData.invoice_filename && (
                <span className="font-medium"> ({ocrPrefillData.invoice_filename})</span>
              )}. 
              The invoice will be automatically attached as evidence when you save.
            </p>
          </div>
        </div>
      )}

      <EmissionFormSection
        title="Primary information"
        description="Choose the facility, emission source, and calculation path."
        testId="emission-primary-information-section"
        className="pb-5"
      >
        <Step1BasicSelection
          facilityId={facilityId}
          setFacilityId={setFacilityId}
          facilities={facilities}
          scope={scope}
          setScope={setScope}
          dynamicScopes={dynamicScopes}
          disabledScopes={ghgFormArchitecture.organizationUiConfig.disabledScopes}
          hasScope3Access={hasScope3Access}
          setCategory={setCategory}
          onCategoryChange={handleCategoryChange}
          setFuelId={setFuelId}
          setScope3Method={setScope3Method}
          allocationMethod={allocationMethod}
          onC8AllocationMethodChange={handleC8AllocationMethodChange}
          requiresAssetName={requiresAssetName}
          assetName={assetName}
          setAssetName={setAssetName}
          spendCurrencyConversionMethod={spendCurrencyConversionMethod}
          setSpendCurrencyConversionMethod={setSpendCurrencyConversionMethod}
          setScope3ActivityType={setScope3ActivityType}
          setScope3ActivityId={setScope3ActivityId}
          setUseCustomFuel={setUseCustomFuel}
          setBiogenicScopeSelection={setBiogenicScopeSelection}
          setScope3Subcategory={setScope3Subcategory}
          biogenicScopeSelection={biogenicScopeSelection}
          loadingBiogenicCategories={loadingBiogenicCategories}
          category={category}
          categoriesForScope={categoriesForScope}
          capabilities={resolvedCapabilities}
          scope3Method={scope3Method}
          availableScope3Methods={availableScope3Methods}
          getMethodLabel={getMethodLabel}
          scope3ActivityType={scope3ActivityType}
          availableScope3ActivityTypes={availableScope3ActivityTypes}
          requiresSubcategory={requiresSubcategory}
          availableSubcategories={availableSubcategories}
          scope3Subcategory={scope3Subcategory}
          typeOfProduct={typeOfProduct}
          setTypeOfProduct={setTypeOfProduct}
          scope3ActivityId={scope3ActivityId}
          scope3EFData={scope3EFData}
          reportingYear={reportingYear}
          filteredScope3Activities={filteredScope3Activities}
          useCustomActivity={useCustomActivity}
          setUseCustomActivity={setUseCustomActivity}
          scope3CustomActivity={scope3CustomActivity}
          setScope3CustomActivity={setScope3CustomActivity}
          fuelSearchTerm={fuelSearchTerm}
          setFuelSearchTerm={setFuelSearchTerm}
          loadingScope3EF={loadingScope3EF}
          fuelId={fuelId}
          useCustomFuel={useCustomFuel}
          customFuelName={customFuelName}
          setCustomFuelName={setCustomFuelName}
          customEmissionFactor={customEmissionFactor}
          setCustomEmissionFactor={setCustomEmissionFactor}
          customEmissionFactorUnit={customEmissionFactorUnit}
          setCustomEmissionFactorUnit={setCustomEmissionFactorUnit}
          customSource={customSource}
          setCustomSource={setCustomSource}
          selectedFuel={selectedFuel}
          filteredFuelsForCategory={filteredFuelsForCategory}
          getAvailableEFUnits={getAvailableEFUnits}
          getQuantityUnitFromEFUnit={getQuantityUnitFromEFUnit}
          // KPI Access Control
          kpiCanAccessScope={kpiCanAccessScope}
          kpiAllowedScopes={kpiAllowedScopes}
          filterFacilitiesByScope={filterFacilitiesByScope}
          hasFullKPIAccess={hasFullKPIAccess}
          // Decision field values (for calculation_methodology etc.)
          decisionFieldValues={decisionFieldValues}
          setDecisionFieldValues={setDecisionFieldValues}
        />
      </EmissionFormSection>

      <EmissionFormSection
        testId="emission-reporting-frequency-section"
      >
        <ReportingPeriodControls
          reportingYearType={reportingYearType}
          reportingYear={reportingYear}
          setReportingYear={setReportingYear}
          frequencyType={frequencyType}
          setFrequencyType={setFrequencyType}
          editingEmission={editingEmission}
          setMonthlyData={setMonthlyData}
          setYearlyData={setYearlyData}
          setExpandedMonths={setExpandedMonths}
          assignedReportingPeriod={assignedReportingPeriod}
          onReportingYearChange={resetC6TripsForReportingYear}
          onFrequencyChange={resetC6TripsForFrequency}
        />
      </EmissionFormSection>

      <EmissionFormSection
        testId="emission-activity-data-section"
      >
        <CreateDataRenderer
          showReportingControls={false}
          reportingYearType={reportingYearType}
          reportingYear={reportingYear}
          setReportingYear={setReportingYear}
          frequencyType={frequencyType}
          setFrequencyType={setFrequencyType}
          editingEmission={editingEmission}
          setMonthlyData={setMonthlyData}
          setYearlyData={setYearlyData}
          setExpandedMonths={setExpandedMonths}
          activeMonths={activeMonths}
          monthlyData={monthlyData}
          expandedMonths={expandedMonths}
          yearlyData={yearlyData}
          dynamicInputFields={dynamicInputFields}
          formConfig={formConfig}
          loadingFormConfig={loadingFormConfig}
          getMonthStatus={getMonthStatus}
          filledMonthsCount={filledMonthsCount}
          updateMonthData={updateMonthData}
          getActualYearForMonth={getActualYearForMonth}
          isFutureMonth={isFutureMonth}
          getFieldUnitsForYearly={getFieldUnitsForYearly}
          renderDynamicField={renderDynamicField}
          isC7EmployeeCommuting={isC7EmployeeCommuting}
          scope3Method={scope3Method}
          spendCurrencyConversionMethod={spendCurrencyConversionMethod}
          spendCurrencyDefaults={spendCurrencyDefaults}
          scope3ActivityType={scope3ActivityType}
          scope3ActivityId={scope3ActivityId}
          employees={employees}
          setEmployees={setEmployees}
          employeeMonthlyTotals={employeeMonthlyTotals}
          employeeYearlyTotal={employeeYearlyTotal}
          isCalculatingEmployee={isCalculatingEmployee}
          handleCalculateEmployeeMonth={handleCalculateEmployeeMonth}
          filteredScope3Activities={filteredScope3Activities}
          useCustomActivity={useCustomActivity}
          scope3CustomActivity={scope3CustomActivity}
          scope={scope}
          biogenicScopeSelection={biogenicScopeSelection}
          useCustomFuel={useCustomFuel}
          customFugitiveQuantityUnits={customFugitiveQuantityUnits}
          selectedFuel={selectedFuel}
          centralizedUnits={centralizedUnits}
          defaultUnit={defaultUnit}
          allowedUnits={allowedUnits}
          requiresSubcategory={requiresSubcategory}
          customEmissionFactorUnit={customEmissionFactorUnit}
          customFuelQtyUnit={customFuelQtyUnit}
          calculationMethodology={decisionFieldValues.calculation_methodology || 'using_heat_basis_ncv'}
          getQuantityUnitFromEFUnit={getQuantityUnitFromEFUnit}
          handleEvidenceUpload={handleEvidenceUpload}
          removeEvidence={removeEvidence}
          onC7EvidenceUpload={handleC7EmployeeEvidenceUpload}
          onC7EvidenceRemove={handleC7EmployeeEvidenceRemove}
          isC6MultiTrip={isC6MultiTrip}
          c6Trips={c6Trips}
          addC6Trip={addC6Trip}
          removeC6Trip={removeC6Trip}
          updateC6Trip={updateC6Trip}
          BACKEND_URL={BACKEND_URL}
          isProcessEmissions={ghgFormContext.isProcessCategory}
          category={category}
          capabilities={resolvedCapabilities}
          fieldOptions={resolvedGhgFieldOptions}
        />
      </EmissionFormSection>

      <EmissionFormSection
        title="Optional fields"
        description="Add process ownership, locations, source references, and notes when relevant."
        collapsible
        defaultOpen={false}
        testId="emission-optional-fields-section"
      >
        <Step2ProcessResponsibility
          responsiblePerson={responsiblePerson}
          setResponsiblePerson={setResponsiblePerson}
          responsiblePersonDesignation={responsiblePersonDesignation}
          setResponsiblePersonDesignation={setResponsiblePersonDesignation}
          responsiblePersonContact={responsiblePersonContact}
          setResponsiblePersonContact={setResponsiblePersonContact}
          processNames={processNames}
          addProcessName={addProcessName}
          removeProcessName={removeProcessName}
          updateProcessName={updateProcessName}
          showsLocationFields={showsLocationFields}
          isC7EmployeeCommuting={isC7EmployeeCommuting}
          fromLocation={fromLocation}
          setFromLocation={setFromLocation}
          toLocation={toLocation}
          setToLocation={setToLocation}
          recordSource={recordSource}
          setRecordSource={setRecordSource}
          scope={scope}
          category={category}
          capabilities={resolvedCapabilities}
          supplierName={supplierName}
          setSupplierName={setSupplierName}
          supplierCode={supplierCode}
          setSupplierCode={setSupplierCode}
        />
        <Step4Notes
          notes={notes}
          setNotes={setNotes}
          selectedFacility={selectedFacility}
          scope={scope}
          category={category}
          scope3Method={scope3Method}
          useCustomActivity={useCustomActivity}
          scope3CustomActivity={scope3CustomActivity}
          filteredScope3Activities={filteredScope3Activities}
          scope3ActivityId={scope3ActivityId}
          requiresSubcategory={requiresSubcategory}
          scope3Subcategory={scope3Subcategory}
          useCustomFuel={useCustomFuel}
          customFuelName={customFuelName}
          selectedFuel={selectedFuel}
          reportingYear={reportingYear}
          frequencyType={frequencyType}
          filledMonthsCount={filledMonthsCount}
          responsiblePerson={responsiblePerson}
          responsiblePersonDesignation={responsiblePersonDesignation}
          responsiblePersonContact={responsiblePersonContact}
          processNames={processNames}
          biogenicScopeSelection={biogenicScopeSelection}
          getMethodLabel={getMethodLabel}
        />
      </EmissionFormSection>

      <div className="flex flex-col-reverse gap-3 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between" data-testid="emission-form-actions">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          data-testid="emission-form-cancel-button"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          className="bg-emerald-700 hover:bg-emerald-800"
          disabled={isSaving}
          data-testid="emission-form-save-button"
        >
          {isSaving ? (
            <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Saving...</>
          ) : (
            <><Check className="mr-1 h-4 w-4" />{isC6MultiTrip ? `Save ${c6TripCount} business travel trip${c6TripCount === 1 ? '' : 's'}` : frequencyType === 'yearly' ? 'Save annual emissions' : `Save emissions (${filledMonthsCount} months)`}</>
          )}
        </Button>
      </div>
    </div>
  );
}
